// src/components/healthrecords/TriageReportModal.js
//
// Previu Smart Report — 6 tabs matching the HTML template design.
// PDF download opens a dedicated print window (not window.print on the main page).
// Biomarker hero cards match the template's bm-card design.

import React, { useState, useEffect, useCallback, useRef, Component } from 'react';
import { createPortal } from 'react-dom';
import { buildOverallTrajectory, overallTrajectoryHtml } from '../../utils/overallTrajectory';
import { HealthScoreHero } from '../../pages/PatientTodayView';
import { healthJourneyAPI } from '../../services/api';
import { healthRecordAPI } from '../../services/api';
import Sparkline from '../Sparkline';
import { useAuth } from '../../context/AuthContext';
import { resolveNavigatorId } from '../../utils/currentNavigator';
import PatientConfoundsTab from './PatientConfoundsTab';
import { buildPrintHtml } from '../../utils/triageReportPrint';
import { systemDisplayName } from '../../utils/systemLabels';
import { useReportConfig } from '../../utils/reportConfig';
import {
  buildConvergence,
  buildDiseaseRisks,
  buildTrajectoryProjection,
  buildYScale,
  isHigherBetter,
  priorDraws,
  lifestyleReviewers,
  buildHeadingInfo,
  buildMarkerChartData,
  trajectoryCoverage,
  trajectoryGapNote,
  MIN_PROJECTION_GAP_MONTHS,
  matchesMarkerName,
  buildActionPlan,
  deserializeActionPlan,
  deserializeCheckedState,
  toggleCheckedState,
  groupByPriority,
  serializeActionPlan,
} from '../../utils/triageUtils';
import { buildWhatsAppDraft, openWhatsAppDraft } from '../../utils/whatsappUtils';
import { watiAPI } from '../../services/api';
import ReportReviewStatus from '../ReportReviewStatus';

// ErrorBoundary for Disease Risk tab — Layer 1 IP surface.
// claudeRawResponse parsing involves multiple steps; any failure degrades gracefully.
class RiskErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err, info) { console.error('RiskTab render error:', err, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding:'32px', textAlign:'center', borderRadius:8,
          background:'var(--rpt-bg)', border:'1px solid var(--rpt-rule)',
        }}>
          <div style={{ fontSize:28, marginBottom:10 }}>&#9888;&#65039;</div>
          <div style={{ fontWeight:700, color:'var(--rpt-navy)', marginBottom:6 }}>
            Disease risk could not be rendered
          </div>
          <div style={{ fontSize:12, color:'var(--rpt-ink-4)' }}>
            The AI analysis data could not be parsed. Please re-run triage to regenerate this report.
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const rl = (level) => {
  if (!level) return 'unknown';
  const s = level.toString().toLowerCase();
  if (s === 'high') return 'high';
  if (s === 'moderate' || s === 'medium') return 'moderate';
  if (s === 'low') return 'low';
  return 'unknown';
};

/**
 * V105: which model actually decides consensus for THIS triage.
 * 'CLAUDE' | 'DETERMINISTIC' | null (row predates V105 — always Claude/RF
 * under the old hardcoded rule, so callers should treat null like 'CLAUDE').
 *
 * Mirrors the backend's RiskModelConfig.resolve(): activeModels=BOTH defers
 * to escalationModel; a single active model IS the escalation model.
 */
const resolvedRiskModel = (t) => {
  if (!t?.activeModels) return null;
  return t.activeModels === 'BOTH' ? t.escalationModel : t.activeModels;
};

/**
 * Model 3 — the deterministic engine.
 *
 * UPDATE (Sep 2026, V105): no longer unconditionally shadow. It is recorded
 * on every triage, but now drives consensusRiskScore/consensusRiskLevel
 * whenever THIS triage's resolved model (see resolvedRiskModel above) is
 * DETERMINISTIC — a per-triage choice, not a platform-wide one. This helper
 * exists so all three places that render Model 3 (the consensus strip, the
 * header summary, and the PDF export) agree on how a verdict reads either
 * way.
 *
 * THE NULL CASE IS THE IMPORTANT ONE. deterministicEvaluatedAt is null when
 * the evaluation never ran — the triage predates the feature, or the
 * evaluator failed open. That must render as "not evaluated" and NEVER as a
 * score, because a missing verdict and a low verdict mean opposite things to
 * a navigator. Returning v:null lets each call site fall back to its own
 * em-dash rather than inventing a zero.
 */
const model3 = (t) => {
  if (!t || !t.deterministicEvaluatedAt) {
    return { v: null, rl: null, sub: 'not evaluated', title: 'Deterministic engine did not run for this triage' };
  }
  // Read alongside the score: a low score from 3 markers means something
  // different from a low score from 39.
  // Whether this triage's resolved model is DETERMINISTIC vs Claude is
  // deliberately NOT surfaced here — governance sign-off (Dr Ajay Kumar +
  // Rakesh Menon) on the Deterministic Model is still pending, and they've
  // asked that the report not editorialise its consensus status until
  // that sign-off lands. It still affects the *actual* consensus
  // computation server-side; this UI just doesn't call attention to it.
  const bits = [];
  if (t.deterministicMarkersScored != null) bits.push(`${t.deterministicMarkersScored} markers`);
  if (t.deterministicConvergence  != null) bits.push(`${t.deterministicConvergence} system${t.deterministicConvergence === 1 ? '' : 's'}`);
  return {
    v:     t.deterministicRiskScore,
    rl:    t.deterministicRiskLevel,
    sub:   bits.length ? bits.join(' · ') : null,
    title: t.deterministicRationale || '',
  };
};

/** Headline risk score: the deterministic model's only. Null when it never ran. */
const headlineScore = (t) =>
  t?.deterministicEvaluatedAt && t.deterministicRiskScore != null ? t.deterministicRiskScore : null;

/**
 * Parses deterministic_feature_flags (V95) into { scored, unscored, systems }.
 *
 * Always returns the three keys so callers never branch on shape — an absent
 * or unparseable document is an empty panel, not a crash. Older triages have
 * a verdict but no evidence (evaluated before V95), which renders as the
 * score with no marker list rather than as an error.
 */
const parseModel3Evidence = (raw, claudeRaw) => {
  const empty = { scored: [], unscored: [], systems: {} };
  if (!raw) return empty;
  try {
    const doc = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const scored   = Array.isArray(doc?.scored)   ? doc.scored   : [];
    const unscored = Array.isArray(doc?.unscored) ? doc.unscored : [];
    const filledScored   = fillMissingValues(scored, claudeRaw);
    const filledUnscored = fillMissingValues(unscored, claudeRaw);
    return {
      scored:   filledScored,
      unscored: [...filledUnscored, ...extractedNotInPanel(filledScored, filledUnscored, claudeRaw)],
      systems:  doc?.systems && typeof doc.systems === 'object' ? doc.systems : {},
    };
  } catch { return empty; }
};

/**
 * Markers the lab report had that the scoring panel never saw. The panel is built from
 * registry-coded markers only, so a result with no registry code (total leucocyte count,
 * RBC, the differential counts...) is in the extracted markers but in neither scored nor
 * unscored, and used to vanish from the Markers tab. They are listed as not scored, so
 * what was read from the report is what the reviewer sees.
 */
const extractedNotInPanel = (scored, unscored, claudeRaw) => {
  const extracted = parseJson(claudeRaw)?.markers;
  if (!Array.isArray(extracted)) return [];
  const known = [...scored, ...unscored].filter(Boolean);
  const codes = new Set(known.map(r => r.code).filter(Boolean));
  const extras = [];
  extracted.forEach(m => {
    const name = m?.name;
    if (!name || m.value == null || String(m.value).trim() === '') return;
    if (m.canonical_code && codes.has(m.canonical_code)) return;
    if (known.some(r => r.name && matchesMarkerName(r.name, name))) return;
    if (extras.some(r => matchesMarkerName(r.name, name))) return;
    extras.push({ code: m.canonical_code || null, name, value: m.value, unit: m.unit || '', reason: 'NOT_IN_PANEL' });
  });
  return extras;
};

/**
 * Evidence rows saved before the value/unit fields were added (early Sep 2026
 * evaluations) have a code and severity but no reading. Fill them from the SAME
 * triage's own extracted markers, joined on canonical_code, so a row never shows
 * another draw's number. Only an unambiguous join is used: if two extracted
 * markers share a code (e.g. a ratio mis-tagged with a component's code) the
 * row stays blank rather than guessing.
 */
const fillMissingValues = (rows, claudeRaw) => {
  if (!rows.some(r => r && r.value == null && r.code)) return rows;
  const markers = parseJson(claudeRaw)?.markers;
  if (!Array.isArray(markers)) return rows;
  const byCode = {};
  markers.forEach(m => { if (m?.canonical_code) (byCode[m.canonical_code] ||= []).push(m); });
  return rows.map(r => {
    if (!r || r.value != null || !r.code) return r;
    const hits = byCode[r.code];
    if (!hits || hits.length !== 1 || hits[0].value == null) return r;
    return { ...r, value: hits[0].value, unit: r.unit ?? hits[0].unit ?? null };
  });
};

/** Why a marker could not be scored, in words a clinician reads rather than an enum. */
const UNSCORED_REASON = {
  UNMAPPED_CODE:         'no canonical code',
  EXCLUDED_FROM_SCORING: 'excluded by rule',
  SEVERITY_UNRESOLVED:   'could not be classified',
  NOT_IN_PANEL:          'not in the scoring panel',
};

/** Registry SeverityLevel -> the report's three badge tones. */
const sevLevel = (sev) => {
  const v = String(sev || '').toUpperCase();
  if (v === 'CRITICAL' || v === 'CRISIS' || v === 'RED' || v === 'POSITIVE') return 'high';
  if (v === 'AMBER' || v === 'FLAG' || v === 'WATCHFUL') return 'moderate';
  return 'low';
};

/** Badge tone for a severity word. Same as sevLevel() (so AMBER and WATCHFUL
 *  still group together as Borderline) except WATCHFUL gets its own cool
 *  'info' tone — the two gold pills were hard to tell apart. */
const sevBadgeLevel = (sev) => (String(sev || '').toUpperCase() === 'WATCHFUL' ? 'info' : sevLevel(sev));

const riskColor  = (l) => ({ high:'var(--rpt-bad)', moderate:'var(--rpt-warn)', low:'var(--rpt-good)', unknown:'var(--rpt-ink-4)' })[rl(l)] || 'var(--rpt-ink-4)';
const riskTint   = (l) => ({ high:'var(--rpt-bad-tint)', moderate:'var(--rpt-warn-tint)', low:'var(--rpt-good-tint)', unknown:'var(--rpt-bg-2)' })[rl(l)] || 'var(--rpt-bg-2)';
const statusCls  = (s) => s === 'bad' || s === 'critical' ? 'bad' : s === 'warn' || s === 'elevated' ? 'warn' : s === 'good' || s === 'normal' ? 'good' : 'info';

const fmtDate = (d) => { if (!d) return '—'; return new Date(d).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }); };
const fmtLong = (d) => { if (!d) return '—'; return new Date(d).toLocaleDateString('en-IN', { day:'numeric', month:'long', year:'numeric' }); };

const parseJson = (raw) => {
  if (!raw) return null;
  try { return JSON.parse(raw.replace(/```json|```/g,'').trim()); } catch { return null; }
};
const parseList = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; }
};

const buildMarkers = (claudeResult) => {
  if (!claudeResult?.markers) return [];
  return claudeResult.markers.map(m => ({
    name: m.name || '—',
    value: m.value,
    unit: m.unit || '',
    range: m.reference || m.ref || '—',
    // Map Claude's status strings to our 3 tiers: bad / warn / good
    // Claude returns: critical, elevated, borderline, normal, optimal, low, high, abnormal
    status: (['critical','very high','very low','dangerously'].some(s => String(m.status||'').toLowerCase().includes(s))
      ? 'bad'
      : (['elevated','borderline','high','low','above','below','abnormal','outside'].some(s => String(m.status||'').toLowerCase().includes(s))
        ? 'warn'
        : 'good')),
    delta: m.delta || null,
  }));
};

// ── CSS ───────────────────────────────────────────────────────────────────────

const REPORT_CSS = `
:root {
  --rpt-bg:#f0ece4; --rpt-bg-2:#e8e2d8; --rpt-surface:#ffffff; --rpt-surface-2:#f7f4ef;
  --rpt-line:#ddd8cf; --rpt-line-soft:#e8e2d8; --rpt-line-strong:#b8b0a4;
  --rpt-ink:#1a1a2e; --rpt-ink-2:#2d3748; --rpt-ink-3:#4a5568;
  --rpt-ink-4:#8a8f9a; --rpt-ink-5:#b0b3bc;
  /* Aligned with index.css brand tokens — was #b0892c (gold) and #1a2841 (navy) */
  --rpt-gold:#b8843a; --rpt-gold-deep:#8a6020; --rpt-gold-tint:#f5ead8; --rpt-gold-soft:#fdf3e3;
  --rpt-navy:#0d1e35; --rpt-navy-soft:#152844;
  /* Solid navy surfaces (hero card, primary button, avatar). Separate from --rpt-navy, which is also the
     heading-text colour and has to flip to a light value in dark mode. */
  --rpt-fill:#0d1e35;
  /* Aligned with dp-green #1a7a4a and dp-red #9b2c2c — was #3a7a4c and #b0432c */
  --rpt-good:#1a7a4a; --rpt-good-tint:#e8f5ee;
  --rpt-warn:#b8843a; --rpt-warn-tint:#fdf3e3;
  --rpt-bad:#9b2c2c;  --rpt-bad-tint:#fdf0f0;
  --rpt-info:#152844; --rpt-info-tint:#e8edf5;
  /* Font stacks — Inter Tight + Cormorant Garamond now preloaded in index.html */
  --rpt-fs:"Inter Tight","DM Sans",-apple-system,sans-serif;
  --rpt-ff:"Cormorant Garamond","Fraunces","Georgia",serif;
  --rpt-fm:"JetBrains Mono","DM Mono",ui-monospace,Menlo,monospace;
}
.rpt-overlay {
  position:fixed; inset:0; background:rgba(26,40,65,.65);
  z-index:10000; display:flex; align-items:stretch; justify-content:center;
  /* Requires viewport-fit=cover in public/index.html's viewport meta tag —
     without it these resolve to 0px and this is a silent no-op. */
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
}
.rpt-overlay.rpt-page { position:static; inset:auto; background:none; z-index:auto; padding:0; }
.rpt-page .rpt-shell { max-width:1200px; margin:0 auto; box-shadow:none; overflow:visible; border-radius:12px; }
.rpt-page .rpt-content { overflow:visible; }
/* Page mode (full-page report): its own header — back link is in the page,
   then eyebrow/title/actions, a patient card and a pill tab bar. The modal
   keeps the old .rpt-chrome bar. */
.rpt-page .rpt-content { padding:0 0 60px; overflow:visible; }
.rpt-page .rpt-sh { margin-top:34px; }
.rpt-page .rpt-sh:first-child { margin-top:0; }
.rpt-page .rpt-footer { margin-top:48px; }
.rpt-hd { display:flex; align-items:flex-end; justify-content:space-between; gap:20px; flex-wrap:wrap; margin:6px 0 28px; }
.rpt-eyebrow { font-family:var(--rpt-fs); font-size:12px; font-weight:600; letter-spacing:.18em; text-transform:uppercase; color:var(--rpt-gold-deep); }
.rpt-title { font-family:var(--rpt-ff); font-weight:600; font-size:46px; line-height:1.1; letter-spacing:-.02em; color:var(--rpt-navy); margin:6px 0 8px; }
.rpt-sub { font-family:var(--rpt-fs); font-size:15px; color:var(--rpt-ink-3); margin:0; }
.rpt-hd-actions { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
.rpt-btn-lg { appearance:none; display:inline-flex; align-items:center; gap:10px; padding:13px 22px; border-radius:14px; font-family:var(--rpt-fs); font-size:15px; font-weight:600; cursor:pointer; border:1px solid var(--rpt-line); background:var(--rpt-surface); color:var(--rpt-navy); box-shadow:0 1px 2px rgba(26,40,65,.04); transition:background .15s,border-color .15s; }
.rpt-btn-lg:hover { border-color:var(--rpt-gold); background:var(--rpt-gold-tint); }
.rpt-btn-lg.primary { background:var(--rpt-fill); border-color:var(--rpt-fill); color:#FBF7EE; }
.rpt-btn-lg.primary:hover { background:var(--rpt-ink-2); }
.rpt-pcard { display:grid; grid-template-columns:1fr auto; align-items:center; gap:24px; background:var(--rpt-surface); border:1px solid var(--rpt-line); border-radius:28px; padding:26px 40px; margin-bottom:22px; box-shadow:0 1px 2px rgba(26,40,65,.04); }
.rpt-pcard-left { display:flex; align-items:center; gap:24px; min-width:0; }
.rpt-pcard .rpt-avatar { width:80px; height:80px; font-size:32px; box-shadow:0 0 0 5px var(--rpt-gold-tint); }
.rpt-pcard-k { font-family:var(--rpt-fs); font-size:11px; font-weight:600; letter-spacing:.16em; text-transform:uppercase; color:var(--rpt-ink-4); }
.rpt-pcard .rpt-pname { font-size:32px; font-weight:600; margin:2px 0 6px; }
.rpt-pcard .rpt-pmeta { text-transform:uppercase; letter-spacing:.06em; font-size:13px; font-weight:500; }
.rpt-pcard-right { display:flex; align-items:center; gap:36px; flex-wrap:wrap; justify-content:flex-end; }
.rpt-pcard-stat .rpt-ss-l { text-transform:none; letter-spacing:0; font-size:13px; margin-bottom:6px; color:var(--rpt-ink-4); }
.rpt-pcard-stat .rpt-ss-v { font-family:var(--rpt-fs); font-weight:600; font-size:20px; letter-spacing:0; }
.rpt-status { display:inline-flex; align-items:center; gap:10px; border-radius:999px; padding:14px 22px; font-family:var(--rpt-fs); font-size:12.5px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; white-space:nowrap; }
.rpt-status::before { content:''; width:10px; height:10px; border-radius:50%; background:currentColor; }
.rpt-status.bad { background:var(--rpt-bad-tint); color:var(--rpt-bad); }
.rpt-status.warn { background:var(--rpt-warn-tint); color:var(--rpt-gold-deep); }
.rpt-status.good { background:var(--rpt-good-tint); color:var(--rpt-good); }
/* page tab bar: full-width track, navy active pill, optimal-ranges switch on the right */
.rpt-page .rpt-tabs { display:flex; align-items:center; width:100%; padding:7px; gap:6px; background:var(--rpt-surface-2); }
.rpt-page .rpt-tabs .tab-btn { padding:13px 28px; font-size:16px; font-weight:600; }
.rpt-page .rpt-tabs .tab-btn.active { background:var(--rpt-fill); color:#FBF7EE; box-shadow:0 4px 12px rgba(26,40,65,.18); }
.rpt-tab-badge { margin-left:12px; min-width:26px; height:26px; padding:0 8px; display:inline-grid; place-items:center; border-radius:999px; font-size:12px; font-weight:700; background:var(--rpt-bad-tint); color:var(--rpt-bad); }
.rpt-tabs-right { margin-left:auto; padding:0 20px 0 12px; display:inline-flex; align-items:center; gap:12px; font-family:var(--rpt-fs); font-size:15px; font-weight:600; color:var(--rpt-ink-2); white-space:nowrap; cursor:pointer; flex-shrink:0; }
.rpt-switch { appearance:none; position:relative; width:46px; height:28px; margin:0; border-radius:999px; background:var(--rpt-line-strong); cursor:pointer; transition:background .18s; }
.rpt-switch::after { content:''; position:absolute; top:3px; left:3px; width:22px; height:22px; border-radius:50%; background:var(--rpt-surface); box-shadow:0 1px 3px rgba(26,40,65,.3); transition:transform .18s; }
.rpt-switch:checked { background:var(--rpt-gold); }
.rpt-switch:checked::after { transform:translateX(18px); }
/* Staff/Patient switch as a segmented pill */
.rpt-seg { display:inline-flex; gap:2px; padding:3px; margin-right:6px; background:var(--rpt-bg-2); border:1px solid var(--rpt-line); border-radius:999px; }
.rpt-seg .rpt-btn { border:0; background:transparent; border-radius:999px; padding:6px 14px; color:var(--rpt-ink-3); }
.rpt-seg .rpt-btn:hover { background:transparent; color:var(--rpt-ink); }
.rpt-seg .rpt-btn.primary { background:var(--rpt-fill); color:#FBF7EE; box-shadow:0 1px 2px rgba(26,40,65,.2); }
.rpt-shell {
  background:var(--rpt-bg); width:100%; max-width:1200px;
  display:flex; flex-direction:column; overflow:hidden;
  box-shadow:0 32px 80px rgba(26,40,65,.35);
}
/* chrome */
.rpt-chrome {
  background:var(--rpt-surface); border-bottom:1px solid var(--rpt-line);
  padding:12px 28px; display:flex; align-items:center; justify-content:space-between;
  flex-shrink:0; gap:14px; font-family:var(--rpt-fs);
}
.rpt-brand { display:flex; align-items:center; gap:12px; }
.rpt-brand-mark {
  width:30px; height:30px; border:1.5px solid var(--rpt-gold); border-radius:50%;
  display:grid; place-items:center; color:var(--rpt-gold-deep);
  font-family:var(--rpt-ff); font-style:italic; font-weight:600; font-size:17px; line-height:1;
}
.rpt-brand-name { font-family:var(--rpt-ff); font-weight:600; font-size:17px; letter-spacing:-.01em; color:var(--rpt-navy); }
.rpt-brand-sub { font-size:9.5px; color:var(--rpt-ink-4); text-transform:uppercase; letter-spacing:.14em; margin-top:2px; }
.rpt-chrome-meta { font-size:11.5px; color:var(--rpt-ink-3); display:flex; gap:14px; align-items:center; }
.rpt-chrome-meta strong { color:var(--rpt-ink); font-weight:600; }
.rpt-chrome-sep { width:1px; height:12px; background:var(--rpt-line-strong); }
.rpt-chrome-actions { display:flex; gap:8px; }
.rpt-btn {
  appearance:none; border:1px solid var(--rpt-line-strong); background:var(--rpt-surface);
  border-radius:7px; padding:6px 13px; font-size:12px; font-weight:500; color:var(--rpt-ink);
  cursor:pointer; display:inline-flex; align-items:center; gap:6px; font-family:var(--rpt-fs);
  transition:background .15s,border-color .15s,color .15s;
}
.rpt-btn:hover { background:var(--rpt-gold-tint); border-color:var(--rpt-gold); color:var(--rpt-gold-deep); }
.rpt-btn.primary { background:var(--rpt-fill); color:#FBF7EE; border-color:var(--rpt-fill); }
.rpt-btn.primary:hover { background:var(--rpt-ink-2); }
.rpt-btn.ghost { background:transparent; border-color:transparent; color:var(--rpt-ink-3); }
.rpt-btn.ghost:hover { background:var(--rpt-bg-2); border-color:var(--rpt-line); color:var(--rpt-ink); }
/* scrollable area */
.rpt-content { flex:1; overflow-y:auto; -webkit-overflow-scrolling:touch; padding:24px 32px 60px; }
/* patient header */
.rpt-ph {
  background:var(--rpt-surface); border:1px solid var(--rpt-line); border-radius:16px;
  padding:20px 24px; display:grid; grid-template-columns:1fr auto; gap:24px;
  align-items:center; margin-bottom:20px;
}
.rpt-ph-left { display:flex; align-items:center; gap:18px; }
.rpt-avatar {
  width:56px; height:56px; border-radius:50%; background:var(--rpt-fill);
  display:grid; place-items:center; color:var(--rpt-gold);
  font-family:var(--rpt-ff); font-style:italic; font-weight:600; font-size:24px;
  border:1.5px solid var(--rpt-gold); flex-shrink:0;
}
.rpt-pname { font-family:var(--rpt-ff); font-weight:500; font-size:22px; letter-spacing:-.01em; margin:0 0 5px; color:var(--rpt-navy); }
.rpt-pmeta { font-size:12px; color:var(--rpt-ink-3); display:flex; gap:10px; flex-wrap:wrap; align-items:center; font-family:var(--rpt-fs); }
.rpt-dot { width:3px; height:3px; background:var(--rpt-ink-5); border-radius:50%; }
.rpt-ph-right { display:flex; gap:28px; align-items:center; }
.rpt-ring { display:flex; align-items:center; gap:12px; }
.rpt-ring svg { display:block; }
.rpt-ring-score { font-family:var(--rpt-ff); font-weight:500; font-size:38px; letter-spacing:-.02em; line-height:1; color:var(--rpt-navy); }
.rpt-ring-score sup { font-size:12px; color:var(--rpt-ink-4); font-weight:500; font-family:var(--rpt-fs); }
.rpt-ring-label { font-size:9.5px; text-transform:uppercase; letter-spacing:.14em; color:var(--rpt-ink-4); margin-top:4px; }
.rpt-ring-band { font-size:11.5px; margin-top:4px; font-weight:600; }
.rpt-sum-stats { display:flex; gap:18px; padding-left:20px; border-left:1px solid var(--rpt-line); }
.rpt-ss-l { font-size:9.5px; text-transform:uppercase; letter-spacing:.12em; color:var(--rpt-ink-4); margin-bottom:3px; }
.rpt-ss-v { font-family:var(--rpt-ff); font-weight:500; font-size:19px; letter-spacing:-.01em; color:var(--rpt-navy); }
.rpt-ss-v.bad { color:var(--rpt-bad); } .rpt-ss-v.warn { color:var(--rpt-gold-deep); } .rpt-ss-v.good { color:var(--rpt-good); }
/* tabs — pill look comes from the shared .tabs-bar/.tab-btn in index.css (same
   control as Recommendation and Escalations); this only adds what a 9-tab strip needs:
   it scrolls sideways instead of overflowing on a phone. */
.rpt-tabs {
  margin-bottom:26px;
  overflow-x:auto; -webkit-overflow-scrolling:touch;
  scrollbar-width:none;
}
.rpt-tabs::-webkit-scrollbar { display:none; }
.rpt-tabs .tab-btn { white-space:nowrap; flex-shrink:0; }
/* section */
.rpt-sh { display:flex; align-items:flex-end; justify-content:space-between; margin:24px 0 10px; }
.rpt-sh:first-child { margin-top:0; }
.rpt-st { font-size:11px; text-transform:uppercase; letter-spacing:.16em; color:var(--rpt-navy); font-weight:600; font-family:var(--rpt-fs); }
.rpt-st .a { color:var(--rpt-gold-deep); }
.rpt-ss { font-size:12px; color:var(--rpt-ink-4); }
/* card */
.rpt-card { background:var(--rpt-surface); border:1px solid var(--rpt-line); border-radius:11px; padding:15px; box-shadow:0 1px 2px rgba(26,40,65,.04); }
/* badge */
.rpt-badge {
  display:inline-flex; align-items:center; gap:4px; font-size:10px; font-weight:600;
  padding:2px 9px; border-radius:999px; letter-spacing:.04em; white-space:nowrap;
  text-transform:uppercase; font-family:var(--rpt-fs);
}
.rpt-badge.good { background:var(--rpt-good-tint); color:var(--rpt-good); }
.rpt-badge.warn { background:var(--rpt-warn-tint); color:var(--rpt-gold-deep); }
.rpt-badge.bad  { background:var(--rpt-bad-tint);  color:var(--rpt-bad); }
.rpt-badge.info { background:var(--rpt-info-tint); color:var(--rpt-info); }
/* score bar */
.rpt-bar-track { height:5px; background:var(--rpt-bg-2); border-radius:999px; overflow:hidden; margin:7px 0 3px; }
.rpt-bar-fill  { height:100%; border-radius:999px; transition:width 1s cubic-bezier(.16,1,.3,1); }
/* AI box */
.rpt-ai {
  background:var(--rpt-fill); color:#F4EEE3; border-radius:16px; padding:22px 26px;
  display:grid; grid-template-columns:auto 1fr; gap:18px; align-items:start;
  margin:18px 0 8px; position:relative; overflow:hidden;
  box-shadow:0 10px 28px -10px rgba(26,40,65,.24);
}
.rpt-ai::before {
  content:""; position:absolute; inset:0;
  background:radial-gradient(circle at 100% 0%,rgba(176,137,44,.18),transparent 55%),
             radial-gradient(circle at 0% 100%,rgba(176,137,44,.08),transparent 50%);
  pointer-events:none;
}
.rpt-ai > * { position:relative; z-index:1; }
.rpt-ai-glyph { width:36px; height:36px; border-radius:50%; border:1px solid var(--rpt-gold); display:grid; place-items:center; color:var(--rpt-gold); font-size:15px; flex-shrink:0; }
.rpt-ai-lbl { font-size:9.5px; text-transform:uppercase; letter-spacing:.18em; color:var(--rpt-gold); margin-bottom:7px; font-weight:600; font-family:var(--rpt-fs); }
.rpt-ai-title { font-family:var(--rpt-ff); font-style:italic; font-weight:500; font-size:21px; line-height:1.2; letter-spacing:-.01em; color:#FBF7EE; margin:0 0 7px; }
.rpt-ai-body { font-size:13px; line-height:1.6; color:rgba(244,238,227,.78); }
.rpt-ai-body b { color:var(--rpt-gold); font-weight:600; }
/* consensus strip */
.rpt-consensus {
  background:var(--rpt-surface); border:1px solid var(--rpt-line); border-radius:11px;
  padding:16px 20px; display:flex; align-items:center; justify-content:space-between;
  gap:16px; margin-bottom:18px; flex-wrap:wrap;
}
.rpt-consensus-score { font-family:var(--rpt-ff); font-weight:500; font-size:46px; letter-spacing:-.025em; line-height:1; }
/* ── BIOMARKER HERO CARDS (template bm-card) ── */
.rpt-bm-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:11px; margin-bottom:18px; }
@media(max-width:900px) { .rpt-bm-grid { grid-template-columns:repeat(2,1fr); } }
/* doctor portal inline report header — ReportHeader in DoctorTriageView.jsx.
   Its 3-column grid (score ring | report meta | Claude score + PDF button)
   doesn't wrap on its own, and the report id is one unbroken string with no
   spaces — on a phone width that forced the whole grid wider than the
   viewport, clipping the score ring on the left instead of visibly
   overflowing. Stack to one column and let the report id wrap instead. */
@media(max-width:560px) {
  .rpt-dr-header { grid-template-columns:1fr !important; gap:14px !important; }
  .rpt-dr-stats-col {
    border-left:none !important; padding-left:0 !important;
    border-top:1px solid var(--rpt-line); padding-top:12px;
  }
}
.rpt-bm {
  background:var(--rpt-surface); border:1px solid var(--rpt-line); border-radius:11px;
  padding:14px 14px 12px; position:relative; overflow:hidden; box-shadow:0 1px 2px rgba(26,40,65,.04);
  transition:border-color .2s;
}
.rpt-bm:hover { border-color:var(--rpt-gold); }
.rpt-bm-name {
  font-size:10px; font-weight:600; color:var(--rpt-ink-3); text-transform:uppercase;
  letter-spacing:.12em; margin-bottom:10px; display:flex; align-items:center; justify-content:space-between;
  font-family:var(--rpt-fs);
}
.rpt-bm-val {
  font-family:var(--rpt-ff); font-weight:500; font-size:30px; letter-spacing:-.025em;
  color:var(--rpt-navy); line-height:1; display:flex; align-items:baseline; gap:3px;
}
.rpt-bm-unit { font-size:11px; color:var(--rpt-ink-4); font-weight:500; letter-spacing:0; font-family:var(--rpt-fs); }
.rpt-bm-foot { display:flex; align-items:center; justify-content:space-between; margin-top:10px; font-size:11px; color:var(--rpt-ink-4); }
.rpt-bm-range { font-family:var(--rpt-fm); font-size:10px; color:var(--rpt-ink-4); letter-spacing:-.02em; }
.rpt-bm-range::before { content:"ref "; opacity:.5; }
/* marker rows (full table) */
.rpt-panel { background:var(--rpt-surface); border:1px solid var(--rpt-line); border-radius:16px; padding:18px 22px 8px; margin-bottom:12px; }
.rpt-panel-head { display:flex; align-items:center; justify-content:space-between; border-bottom:1px solid var(--rpt-line-soft); padding-bottom:12px; margin-bottom:2px; }
.rpt-panel-title { font-family:var(--rpt-ff); font-weight:500; font-size:18px; letter-spacing:-.01em; color:var(--rpt-navy); margin:0; }
.rpt-panel-summary { font-size:11px; color:var(--rpt-ink-4); display:flex; gap:12px; text-transform:uppercase; letter-spacing:.07em; font-family:var(--rpt-fs); }
.rpt-panel-summary b { color:var(--rpt-navy); }
.rpt-mrow {
  display:grid; grid-template-columns:1.8fr 1fr 1.2fr .8fr; align-items:center;
  padding:11px 4px; border-bottom:1px solid var(--rpt-line-soft); font-size:12.5px; gap:10px;
}
.rpt-mrow:last-child { border-bottom:0; }
.rpt-mrow:hover { background:var(--rpt-surface-2); }
/* Marker row (Markers tab cards): two lines on a phone — name + badge, then
   value + reference range — and the four-column table row on wide screens.
   No marker codes (CARD.HCY etc.): they were noise to the reader. */
.rpt-mk {
  display:grid; grid-template-columns:minmax(0,1fr) auto;
  grid-template-areas:"name badge" "value ref";
  align-items:center; gap:5px 14px;
  padding:13px 4px; border-bottom:1px solid var(--rpt-line-soft); font-size:12.5px;
}
.rpt-mk:last-child { border-bottom:0; }
.rpt-mk:hover { background:var(--rpt-surface-2); }
.rpt-mk-name  { grid-area:name; font-weight:500; color:var(--rpt-ink); font-family:var(--rpt-fs); font-size:13px; letter-spacing:.01em; }
.rpt-mk-value { grid-area:value; font-family:var(--rpt-ff); font-weight:500; font-size:18px; letter-spacing:-.01em; color:var(--rpt-navy); }
.rpt-mk-value small { font-family:var(--rpt-fs); font-weight:400; font-size:11px; color:var(--rpt-ink-4); margin-left:3px; letter-spacing:0; }
.rpt-mk-ref   { grid-area:ref; justify-self:end; text-align:right; font-family:var(--rpt-fm); font-size:10.5px; color:var(--rpt-ink-4); }
.rpt-mk-ref::before { content:"ref "; opacity:.5; }
.rpt-mk-badge { grid-area:badge; justify-self:end; }
@media (min-width:720px) {
  .rpt-mk { grid-template-columns:1.8fr 1fr 1.2fr .8fr; grid-template-areas:"name value ref badge"; gap:10px; padding:11px 4px; }
  .rpt-mk-ref { justify-self:start; text-align:left; }
}
.rpt-mn { font-weight:500; color:var(--rpt-ink); font-family:var(--rpt-fs); }
.rpt-mn small { display:block; font-size:10.5px; color:var(--rpt-ink-4); font-weight:400; margin-top:1px; }
.rpt-mv { font-family:var(--rpt-ff); font-weight:500; font-size:16px; letter-spacing:-.01em; color:var(--rpt-navy); }
.rpt-mv small { font-family:var(--rpt-fs); font-weight:400; font-size:10.5px; color:var(--rpt-ink-4); margin-left:2px; letter-spacing:0; }
.rpt-ref { font-family:var(--rpt-fm); font-size:10.5px; color:var(--rpt-ink-4); }
.rpt-ref::before { content:"ref "; opacity:.5; }
.rpt-ref.rpt-ref-plain::before { content:none; }
.rpt-obs { font-family:var(--rpt-fs); font-size:12.5px; line-height:1.6; color:var(--rpt-ink); padding:2px 2px 6px; }
.rpt-obs-line { padding:2px 0; }
.rpt-obs-title { font-weight:600; font-size:13.5px; margin-bottom:4px; }
.rpt-obs-gap { height:10px; }
.rpt-obs-cell + .rpt-obs-cell::before { content:"·"; color:var(--rpt-ink-4); margin:0 9px; }
/* risk cards */
.rpt-risk-grid { display:grid; grid-template-columns:1fr 1fr; gap:11px; }
@media(max-width:720px) { .rpt-risk-grid { grid-template-columns:1fr; } }
.rpt-risk-card { background:var(--rpt-surface); border:1px solid var(--rpt-line); border-radius:11px; padding:16px 18px; position:relative; transition:border-color .2s; }
.rpt-risk-card:hover { border-color:var(--rpt-gold); }
.rpt-risk-head { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:3px; gap:12px; }
.rpt-risk-title { font-family:var(--rpt-ff); font-weight:500; font-size:18px; letter-spacing:-.01em; color:var(--rpt-navy); margin:0; line-height:1.15; }
.rpt-risk-pct { font-family:var(--rpt-ff); font-weight:500; font-size:34px; letter-spacing:-.025em; line-height:1; }
.rpt-risk-pct sup { font-family:var(--rpt-fs); font-size:14px; font-weight:500; }
.rpt-risk-window { font-size:10.5px; text-transform:uppercase; letter-spacing:.1em; color:var(--rpt-ink-4); margin-bottom:1px; font-family:var(--rpt-fs); }
.rpt-risk-body { font-size:12.5px; color:var(--rpt-ink-2); line-height:1.55; margin-top:10px; }
.rpt-contrib { display:flex; flex-wrap:wrap; gap:4px; margin-top:10px; }
/* action plan */
.rpt-action {
  background:var(--rpt-surface); border:1px solid var(--rpt-line); border-radius:11px;
  padding:14px 16px; margin-bottom:9px;
  display:grid; grid-template-columns:auto 1fr auto; gap:14px; align-items:start;
}
.rpt-action:hover { border-color:var(--rpt-gold); }
.rpt-pb { width:46px; padding:5px 0; border-radius:999px; display:grid; place-items:center; font-weight:600; font-size:10px; letter-spacing:.14em; text-transform:uppercase; font-family:var(--rpt-fs); }
.rpt-pb.p1 { background:var(--rpt-bad); color:#FBF7EE; }
.rpt-pb.p2 { background:var(--rpt-warn); color:#FBF7EE; }
.rpt-pb.p3 { background:var(--rpt-info); color:#FBF7EE; }
.rpt-acat { font-size:10px; text-transform:uppercase; letter-spacing:.12em; color:var(--rpt-gold-deep); font-weight:600; margin-bottom:3px; font-family:var(--rpt-fs); }
.rpt-atitle { font-family:var(--rpt-ff); font-weight:500; font-size:16px; letter-spacing:-.005em; color:var(--rpt-navy); margin:0 0 4px; line-height:1.2; }
.rpt-abody { font-size:12px; color:var(--rpt-ink-3); line-height:1.55; margin:0; }
.rpt-awin { text-align:right; font-size:10.5px; color:var(--rpt-ink-4); text-transform:uppercase; letter-spacing:.1em; font-family:var(--rpt-fs); white-space:nowrap; }
.rpt-awin strong { display:block; font-family:var(--rpt-ff); font-style:italic; font-size:15px; color:var(--rpt-navy); text-transform:none; font-weight:500; margin-top:3px; }
/* chips */
.rpt-chip { font-size:10.5px; background:var(--rpt-bg-2); color:var(--rpt-ink-3); border-radius:999px; padding:2px 9px; display:inline-block; font-family:var(--rpt-fs); }
.rpt-chips { display:flex; flex-wrap:wrap; gap:4px; margin-top:7px; }
/* Trajectory tab — one compact card per marker (TrajectoryCard) */
.rpt-traj { --tj-line:var(--rpt-good); --tj-tint:var(--rpt-good-tint); background:var(--rpt-surface); border:1px solid var(--rpt-line); border-radius:24px; padding:18px 22px 16px; margin-bottom:0; box-shadow:0 1px 2px rgba(26,40,65,.04); }
.rpt-traj-bad  { --tj-line:var(--rpt-bad);  --tj-tint:var(--rpt-bad-tint); }
.rpt-traj-warn { --tj-line:var(--rpt-gold-deep); --tj-tint:var(--rpt-warn-tint); }
.rpt-traj-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:16px; align-items:start; margin-bottom:16px; }
@media (max-width:900px) { .rpt-traj-grid { grid-template-columns:1fr; } }
.rpt-traj-head { display:flex; align-items:flex-start; justify-content:space-between; gap:14px; }
.rpt-traj-name { font-family:var(--rpt-fs); font-weight:700; font-size:17px; letter-spacing:-.01em; color:var(--rpt-navy); text-transform:capitalize; line-height:1.2; min-width:0; }
.rpt-traj-now { font-family:var(--rpt-fs); font-weight:700; font-size:34px; line-height:1.1; margin-top:4px; white-space:nowrap; color:var(--rpt-navy); }
.rpt-traj-now small { font-size:13px; font-weight:400; margin-left:6px; color:var(--rpt-ink-4); }
.rpt-traj-delta { font-family:var(--rpt-fs); font-weight:700; font-size:13px; color:var(--tj-line); background:var(--tj-tint); border-radius:999px; padding:6px 14px; white-space:nowrap; }
.rpt-traj-flag { display:inline-block; font-size:9px; font-weight:600; text-transform:uppercase; letter-spacing:.08em; color:var(--rpt-gold-deep); background:var(--rpt-gold-soft); border-radius:999px; padding:1px 8px; margin-top:6px; font-family:var(--rpt-fs); }
.rpt-traj-curve { display:block; width:100%; height:84px; margin-top:12px; }
.rpt-traj-area { fill:var(--tj-tint); }
.rpt-traj-line { fill:none; stroke:var(--tj-line); stroke-width:4; stroke-linecap:round; stroke-linejoin:round; }
.rpt-traj-foot { display:flex; justify-content:space-between; align-items:baseline; gap:10px; margin:8px 0 10px; font-size:12px; color:var(--rpt-ink-3); font-family:var(--rpt-fs); }
.rpt-traj-trend { font-weight:700; text-align:center; }
.rpt-traj-trend.worse { color:var(--rpt-bad); } .rpt-traj-trend.better { color:var(--rpt-good); }
.rpt-traj-note { font-size:12px; color:var(--rpt-ink-3); background:var(--rpt-surface-2); border-radius:10px; padding:10px 12px; margin:12px 0; }
.rpt-fc-k { font-size:9.5px; font-weight:700; letter-spacing:.12em; text-transform:uppercase; color:var(--rpt-ink-4); font-family:var(--rpt-fs); margin:16px 0 6px; }
.rpt-fc-plan { display:inline-flex; align-items:center; gap:6px; font-size:11px; font-weight:600; color:var(--rpt-gold-deep); background:var(--rpt-gold-soft); border:1px solid var(--rpt-gold-tint); border-radius:999px; padding:3px 10px; font-family:var(--rpt-fs); }
.rpt-fc-plan i { width:6px; height:6px; border-radius:50%; background:var(--rpt-gold); }
.rpt-fc-tiles { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-top:6px; }
.rpt-fc-tile { border-radius:12px; padding:10px 12px; border:1px solid transparent; min-width:0; }
.rpt-fc-tile.bad { background:var(--rpt-bad-tint); border-color:var(--rpt-bad-line, var(--rpt-bad-tint)); }
.rpt-fc-tile.good { background:var(--rpt-good-tint); border-color:var(--rpt-good-line, var(--rpt-good-tint)); }
.rpt-fc-tile-l { font-size:9.5px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; font-family:var(--rpt-fs); }
.rpt-fc-tile.bad .rpt-fc-tile-l, .rpt-fc-tile.bad .rpt-fc-tile-d { color:var(--rpt-bad); }
.rpt-fc-tile.good .rpt-fc-tile-l, .rpt-fc-tile.good .rpt-fc-tile-d { color:var(--rpt-good); }
.rpt-fc-tile-v { font-family:var(--rpt-ff); font-size:24px; font-weight:500; line-height:1.1; margin:3px 0 1px; color:var(--rpt-ink); }
.rpt-fc-tile-v small { font-family:var(--rpt-fs); font-size:11px; font-weight:400; color:var(--rpt-ink-4); margin-left:3px; }
.rpt-fc-tile-d { font-size:11px; font-weight:600; font-family:var(--rpt-fs); }
.rpt-fc-same { display:flex; gap:8px; align-items:center; background:var(--rpt-surface-2); border-radius:12px; padding:10px 12px; margin-top:8px; font-size:12px; color:var(--rpt-ink-3); font-family:var(--rpt-fs); }
.rpt-fc-same b { font-family:var(--rpt-ff); font-size:20px; font-weight:500; color:var(--rpt-good); }
@media (max-width:520px) {
  .rpt-traj { padding:18px 16px 16px; border-radius:20px; }
  .rpt-traj-now { font-size:30px; }
  .rpt-traj-foot { font-size:11.5px; }
}
/* Scored Markers group filter — one marker card at a time */
.rpt-filter { display:flex; flex-wrap:wrap; gap:8px; margin:0 0 12px; }
.rpt-filter-btn {
  appearance:none; cursor:pointer; display:inline-flex; align-items:center; gap:7px;
  background:var(--rpt-surface); color:var(--rpt-ink-3); border:1px solid var(--rpt-line);
  border-radius:999px; padding:7px 14px; font-family:var(--rpt-fs); font-size:12px; font-weight:500;
}
.rpt-filter-btn:hover { border-color:var(--rpt-line-strong); color:var(--rpt-ink); }
.rpt-filter-btn.active { background:var(--rpt-fill); border-color:var(--rpt-fill); color:var(--rpt-surface); font-weight:600; }
.rpt-filter-n { font-family:var(--rpt-fm); font-size:11px; opacity:.75; }
/* acute alert */
.rpt-acute { background:linear-gradient(135deg,#8C2A2F 0%,#B0432C 100%); color:#FBF7EE; border-radius:13px; padding:16px 20px; display:grid; grid-template-columns:auto 1fr; gap:14px; align-items:center; margin-bottom:18px; }
.rpt-acute-icon { font-size:22px; }
.rpt-acute-title { font-family:var(--rpt-ff); font-size:17px; font-weight:500; margin:0 0 3px; }
.rpt-acute-body { font-size:12.5px; color:rgba(244,238,227,.85); line-height:1.5; }
/* missing */
.rpt-missing { display:flex; align-items:flex-start; gap:8px; background:var(--rpt-gold-soft); border:1px solid var(--rpt-gold-tint); border-radius:8px; padding:10px 13px; font-size:12.5px; color:#7a5a10; margin-top:12px; }
/* placeholder tab */
.rpt-placeholder { text-align:center; padding:48px 20px; color:var(--rpt-ink-4); }
.rpt-placeholder-icon { font-size:36px; margin-bottom:12px; }
.rpt-placeholder-title { font-family:var(--rpt-ff); font-size:20px; color:var(--rpt-navy); margin-bottom:8px; }
.rpt-placeholder-body { font-size:13px; max-width:400px; margin:0 auto; line-height:1.6; }
/* footer */
.rpt-footer { margin-top:36px; padding-top:18px; border-top:1px solid var(--rpt-line); display:flex; justify-content:space-between; font-size:10.5px; color:var(--rpt-ink-4); letter-spacing:.02em; font-family:var(--rpt-fs); flex-wrap:wrap; gap:8px; }
/* ── PRINT ── */
@media print {
  .rpt-overlay, .rpt-shell { position:static!important; background:none!important; box-shadow:none!important; max-width:100%!important; display:block!important; overflow:visible!important; }
  .rpt-chrome { display:none!important; }
  .rpt-content { overflow:visible!important; padding:0!important; }
  .rpt-tabs { display:none!important; }
  .rpt-tab-panel { display:block!important; }
  body { background:white!important; }
  .rpt-ai, .rpt-acute { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  @page { margin:1.5cm 2cm; }
}

/* ── WhatsApp Draft ── */
.rpt-wa-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: #25d366; color: #fff;
  border: none; border-radius: 8px;
  padding: 5px 12px; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: background .15s; flex-shrink: 0;
}
.rpt-wa-btn:hover { background: #1ebe5b; }

.rpt-wa-overlay {
  position: fixed; inset: 0;
  background: rgba(13,30,53,.55);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; padding: 20px;
}
.rpt-wa-modal {
  background: #fff; border-radius: 16px;
  width: 100%; max-width: 540px;
  box-shadow: 0 24px 60px rgba(0,0,0,.25);
  display: flex; flex-direction: column; overflow: hidden;
}
.rpt-wa-modal-header {
  padding: 20px 22px 14px;
  border-bottom: 1px solid #f0ece4;
}
.rpt-wa-modal-title {
  font-size: 17px; font-weight: 700; color: #0d1e35; margin-bottom: 4px;
}
.rpt-wa-modal-sub {
  font-size: 12px; color: #888; line-height: 1.4;
}
.rpt-wa-textarea {
  flex: 1; padding: 16px 18px;
  border: none; outline: none; resize: vertical;
  font-family: 'DM Mono', 'Courier New', monospace;
  font-size: 13px; line-height: 1.6; color: #0d1e35;
  background: #fafaf8; min-height: 260px;
  border-bottom: 1px solid #f0ece4;
}
.rpt-wa-modal-footer {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 20px;
}
.rpt-wa-cancel-btn {
  background: none; border: 1px solid #e0dbd4;
  border-radius: 8px; padding: 8px 18px;
  font-size: 13px; color: #666; cursor: pointer;
}
.rpt-wa-cancel-btn:hover { background: #f5f1eb; }
.rpt-wa-send-btn {
  background: #25d366; color: #fff; border: none;
  border-radius: 8px; padding: 8px 20px;
  font-size: 13px; font-weight: 700; cursor: pointer;
}
.rpt-wa-send-btn:hover { background: #1ebe5b; }
/* ── Lifestyle Tab (Tab 07) ── */
.rpt-ls-root { padding: 0 0 40px; }
.rpt-ls-badge-row { display: flex; gap: 8px; margin-bottom: 20px; flex-wrap: wrap; }
.rpt-ls-badge {
  font-size: 10px; font-weight: 700; letter-spacing: .07em; text-transform: uppercase;
  padding: 4px 10px; border-radius: 99px;
}
.rpt-ls-badge--layer3 { background: #e8f4fd; color: #1a6fa8; }
.rpt-ls-badge--v57 { background: #f0f7ee; color: #2d7a4f; }
.rpt-ls-card {
  background: var(--rpt-surface); border: 1px solid var(--rpt-line);
  border-radius: 14px; padding: 24px 28px;
  box-shadow: 0 2px 8px rgba(0,0,0,.05); margin-bottom: 18px;
}
.rpt-ls-para {
  font-family: var(--rpt-ff-body, Georgia, serif);
  font-size: 15px; line-height: 1.7; color: var(--rpt-navy);
  margin: 0 0 16px;
}
.rpt-ls-para:last-child { margin-bottom: 0; }
.rpt-ls-disclaimer {
  background: var(--rpt-surface-2); border: 1px solid var(--rpt-line);
  border-radius: 10px; padding: 12px 16px;
  font-size: 12px; color: var(--rpt-ink-3); line-height: 1.55;
}
.rpt-ls-empty { text-align: center; padding: 60px 24px; }
.rpt-ls-empty-icon { font-size: 48px; margin-bottom: 16px; }
.rpt-ls-empty-title {
  font-family: var(--rpt-ff); font-size: 18px; font-weight: 600;
  color: var(--rpt-navy); margin-bottom: 12px;
}
.rpt-ls-empty-body {
  font-size: 14px; color: var(--rpt-ink-3); line-height: 1.6;
  max-width: 420px; margin: 0 auto 12px;
}
.rpt-ls-empty-note { font-size: 12px; color: var(--rpt-ink-4); margin-top: 8px; }
/* The standalone patient Lifestyle page renders this tab straight onto the app
   shell, so in dark mode the report's light palette (white card, navy text, pale
   gold eyebrow) is unreadable. Remap the report tokens to the shared theme tokens
   for that page only; the report modal keeps its own light surface. */
.app-shell[data-theme="dark"] .rpt-ls-root--standalone,
.app-shell[data-theme="dark"] .rpt-ls-empty--standalone {
  --rpt-surface: var(--bg-surface);
  --rpt-surface-2: var(--bg-subtle);
  --rpt-line: var(--border);
  --rpt-navy: var(--text-primary);
  --rpt-ink-3: var(--text-secondary);
  --rpt-ink-4: var(--text-muted);
  --rpt-gold-deep: var(--amber-text);
  --rpt-good: var(--green);
}
.app-shell[data-theme="dark"] .rpt-ls-root--standalone .rpt-ls-card { box-shadow: var(--shadow-sm); }

/* ── DARK ──
   The report modal is portalled to <body>, outside .app-shell, but :has() on the root
   reaches it (same trick index.css uses). Screen only: the print stylesheet and the PDF
   builders (triageReportPrint / patientReportPrint) keep the light palette. */
@media screen {
  :root:has(.app-shell[data-theme="dark"]) {
    --rpt-bg:#0a0d14; --rpt-bg-2:#10141e; --rpt-surface:#141926; --rpt-surface-2:#1a2030;
    --rpt-line:#262d3d; --rpt-line-soft:#1d2331; --rpt-line-strong:#3a4358;
    --rpt-ink:#f3efe6; --rpt-ink-2:#dfe3ec; --rpt-ink-3:#c3c9d6;
    --rpt-ink-4:#8c94a6; --rpt-ink-5:#5d6577;
    --rpt-gold:#d0a050; --rpt-gold-deep:#e0b468; --rpt-gold-tint:rgba(208,160,80,.18); --rpt-gold-soft:rgba(208,160,80,.10);
    --rpt-navy:#f3efe6; --rpt-navy-soft:#c3c9d6; --rpt-fill:#1c2f52;
    --rpt-good:#4cc38a; --rpt-good-tint:rgba(76,195,138,.14);
    --rpt-warn:#d9a24f; --rpt-warn-tint:rgba(217,162,79,.14);
    --rpt-bad:#ef7b7b;  --rpt-bad-tint:rgba(239,123,123,.14);
    --rpt-info:#8fb0e8; --rpt-info-tint:rgba(143,176,232,.14);
  }
  :root:has(.app-shell[data-theme="dark"]) .rpt-overlay { background:rgba(0,0,0,.75); }
  :root:has(.app-shell[data-theme="dark"]) .rpt-shell { box-shadow:0 32px 80px rgba(0,0,0,.6); }
  :root:has(.app-shell[data-theme="dark"]) .rpt-card,
  :root:has(.app-shell[data-theme="dark"]) .rpt-ls-card { box-shadow:none; }
  /* Pills and the active filter sit on a now-light or now-dark fill: keep their text readable. */
  :root:has(.app-shell[data-theme="dark"]) .rpt-pb { color:#05070c; }
  :root:has(.app-shell[data-theme="dark"]) .rpt-filter-btn.active { color:#f3efe6; }
  :root:has(.app-shell[data-theme="dark"]) .rpt-missing { color:var(--rpt-gold-deep); }
  :root:has(.app-shell[data-theme="dark"]) .rpt-ls-badge--layer3 { background:rgba(26,111,168,.22); color:#7fc0ee; }
  :root:has(.app-shell[data-theme="dark"]) .rpt-ls-badge--v57 { background:rgba(45,122,79,.22); color:#6fd09b; }
  /* WhatsApp share dialog */
  :root:has(.app-shell[data-theme="dark"]) .rpt-wa-modal { background:var(--rpt-surface); }
  :root:has(.app-shell[data-theme="dark"]) .rpt-wa-modal-header { border-bottom-color:var(--rpt-line); }
  :root:has(.app-shell[data-theme="dark"]) .rpt-wa-modal-title { color:var(--rpt-ink); }
  :root:has(.app-shell[data-theme="dark"]) .rpt-wa-modal-sub { color:var(--rpt-ink-4); }
  :root:has(.app-shell[data-theme="dark"]) .rpt-wa-textarea { background:var(--rpt-surface-2); color:var(--rpt-ink); border-bottom-color:var(--rpt-line); }
  :root:has(.app-shell[data-theme="dark"]) .rpt-wa-cancel-btn { border-color:var(--rpt-line-strong); color:var(--rpt-ink-3); }
  :root:has(.app-shell[data-theme="dark"]) .rpt-wa-cancel-btn:hover { background:var(--rpt-surface-2); }
}

`;

let _cssInjected = false;
const injectCss = () => {
  if (_cssInjected) return;
  // Fonts (Inter Tight, Cormorant Garamond, JetBrains Mono) are preloaded
  // in public/index.html — no runtime injection needed.
  const style = document.createElement('style');
  style.textContent = REPORT_CSS;
  document.head.appendChild(style);
  _cssInjected = true;
};

// ── Sub-components ────────────────────────────────────────────────────────────

function Badge({ level, label }) {
  const cls = rl(level) === 'high' ? 'bad' : rl(level) === 'moderate' ? 'warn' : rl(level) === 'low' ? 'good' : 'info';
  return <span className={`rpt-badge ${cls}`}>{label || String(level || '—').toUpperCase()}</span>;
}

function ScoreBar({ score, level }) {
  const bg = level === 'high' ? 'linear-gradient(90deg,#B0432C,#D06040)'
           : level === 'moderate' ? 'linear-gradient(90deg,#B0892C,#D4A840)'
           : 'linear-gradient(90deg,#3A7A4C,#5A9A6C)';
  return (
    <div className="rpt-bar-track">
      <div className="rpt-bar-fill" style={{ width:`${score ?? 0}%`, background:bg }} />
    </div>
  );
}

// Biomarker hero card — matches template .bm-card design
function BmCard({ m, readOnly = false }) {
  const badgeCls = statusCls(m.status);
  const trendColor = m.status === 'bad' ? 'var(--rpt-bad)' : m.status === 'warn' ? 'var(--rpt-gold)' : 'var(--rpt-good)';
  return (
    <div className="rpt-bm">
      <div className="rpt-bm-name">
        {m.name}
        {m.delta && <span style={{ fontSize:10.5, color:trendColor, fontWeight:600 }}>{m.delta}</span>}
      </div>
      <div className="rpt-bm-val">
        {m.value ?? '—'}
        {m.unit && <span className="rpt-bm-unit">{m.unit}</span>}
      </div>
      <div className="rpt-bm-foot">
        {!readOnly && <span className="rpt-bm-range">{m.range}</span>}
        <span className={`rpt-badge ${badgeCls}`} style={{ fontSize:9.5, padding:'1px 7px', marginLeft: readOnly ? 'auto' : undefined }}>
          {m.status === 'bad' ? 'OUT OF RANGE' : m.status === 'warn' ? 'BORDERLINE' : 'OPTIMAL'}
        </span>
      </div>
    </div>
  );
}

// ── Tab 01 — Overview ─────────────────────────────────────────────────────────

/** Patient-view header: the report's Health score, 100 − the risk score (the
 *  same framing and arithmetic as the patient app's Health Score graph, so the
 *  two never disagree), drawn like the staff Risk Score ring but climbing as the
 *  patient is better off. Colour follows the underlying risk band. Nothing when
 *  the report has no headline score. */
function HealthScoreRing({ triage }) {
  const risk = headlineScore(triage);
  if (risk == null) return null;
  const health = Math.max(0, Math.min(100, 100 - risk));
  const rs = 7, ringSize = 86;
  const r2 = (ringSize - rs) / 2;
  const C2 = 2 * Math.PI * r2;
  const color = risk >= 70 ? 'var(--rpt-bad)' : risk >= 40 ? 'var(--rpt-warn)' : 'var(--rpt-good)';
  return (
    <div className="rpt-ring" role="img" aria-label={`Health score ${health} out of 100`}>
      <svg width={ringSize} height={ringSize} aria-hidden="true">
        <circle cx={ringSize/2} cy={ringSize/2} r={r2} fill="none" stroke="var(--rpt-line)" strokeWidth={rs} />
        <circle cx={ringSize/2} cy={ringSize/2} r={r2} fill="none" stroke={color} strokeWidth={rs}
          strokeDasharray={C2} strokeDashoffset={C2 * (1 - health / 100)} strokeLinecap="round"
          transform={`rotate(-90 ${ringSize/2} ${ringSize/2})`} />
      </svg>
      <div>
        <div className="rpt-ring-score">{health}<sup>/100</sup></div>
        <div className="rpt-ring-label">Health Score</div>
      </div>
    </div>
  );
}

/** "Flagged by (your/the) doctor": the markers the treating doctor flagged on
 *  this report (triage.highlightedMarkers), shown on the Overview tab in place
 *  of Key Biomarkers. Each is joined to its scored row (m3.scored, by canonical
 *  code) for value / range / severity; a flagged marker the engine did not score
 *  still appears, by name only. Renders nothing when none are flagged. */
function FlaggedMarkersCard({ triage, m3, readOnly = false }) {
  const flagged = (triage?.highlightedMarkers || []).map(h => ({
    h,
    row: (m3?.scored || []).find(m => h.canonicalCode && m.code === h.canonicalCode) || null,
  }));
  if (flagged.length === 0) return null;
  return (
    <div className="rpt-panel" style={{ marginBottom:10 }}>
      <div className="rpt-panel-head">
        <div className="rpt-panel-title" style={{ color:'var(--rpt-gold-deep)' }}>
          {readOnly ? 'Flagged by your doctor' : 'Flagged by the doctor'}
        </div>
        <div className="rpt-panel-summary"><b>{flagged.length}</b> marker{flagged.length === 1 ? '' : 's'}</div>
      </div>
      {flagged.map(({ h, row }, i) => (
        <MarkerRow
          key={h.canonicalCode || h.markerName || i}
          name={(h.markerName || row?.name || h.canonicalCode || '—').toUpperCase()}
          value={row ? (row.value ?? '—') : ''}
          unit={row ? row.unit : null}
          refText={row ? (row.range || '—') : null}
          badge={row ? <Badge level={sevBadgeLevel(row.severity)} label={row.severity || '—'} /> : null}
        />
      ))}
    </div>
  );
}

function OverviewTab({ triage, markers, missing, m3, hideAcuteAlert = false, readOnly = false, scoreGraph = null }) {
  // When the doctor has flagged markers, those replace the generic Key
  // Biomarkers cards; otherwise the Key Biomarkers show as before.
  const hasFlagged = (triage?.highlightedMarkers || []).length > 0;
  // Show top 8 markers as hero cards (worst status first)
  const sorted = [...markers].sort((a,b) => {
    const w = { bad:0, warn:1, good:2 };
    return (w[a.status] ?? 1) - (w[b.status] ?? 1);
  });
  const heroMarkers = sorted.slice(0, 8);

  return (
    <div>
      {/* 20 Jul 2026: hideAcuteAlert — DoctorTriageView.jsx already shows an
          identical acute-alert banner persistently above its tabs
          (always visible, not just on Overview), so passes true here to
          avoid showing it twice. Defaults to false so the navigator's
          TriageReportModal usage (which has no equivalent persistent
          banner of its own) is completely unaffected. */}
      {/* Staff only: a patient never sees the acute-alert banner (readOnly = patient view). */}
      {triage.hasAcuteAlert && !hideAcuteAlert && !readOnly && (
        <div className="rpt-acute">
          <div className="rpt-acute-icon">🚨</div>
          <div>
            <div className="rpt-acute-title">Acute Alert — Immediate Clinical Review Required</div>
            <div className="rpt-acute-body">{triage.acuteAlertDetail}</div>
          </div>
        </div>
      )}

      {/* The Health Score graph from the patient's Overview tab (journey data); only passed when a scored report exists. */}
      {scoreGraph && <div style={{ marginBottom:20 }}>{scoreGraph}</div>}

      {/* Doctor-flagged markers, or (when none are flagged) the key biomarker cards */}
      {hasFlagged && <FlaggedMarkersCard triage={triage} m3={m3} readOnly={readOnly} />}
      {!hasFlagged && heroMarkers.length > 0 && (
        <div>
          <div className="rpt-sh">
            <div className="rpt-st">Key Biomarkers{!readOnly && <span className="a"> · Tier 01</span>}</div>
          </div>
          <div className="rpt-bm-grid">
            {heroMarkers.map((m, i) => <BmCard key={i} m={m} readOnly={readOnly} />)}
          </div>
        </div>
      )}

      {/* Clinical recommendation */}
      {triage.claudeRecommendation && (
        <div>
          <div className="rpt-sh"><div className="rpt-st">Clinical Recommendation</div></div>
          <div className="rpt-card" style={{ fontSize:13.5, lineHeight:1.65, color:'var(--rpt-ink-2)' }}>
            {triage.claudeRecommendation}
          </div>
        </div>
      )}

      {!readOnly && missing.length > 0 && (
        <div className="rpt-missing">
          <span>⚠</span>
          <div>
            <strong>Missing markers not included in scoring:</strong> {missing.join(', ')}
            <div style={{ marginTop:4, fontSize:11.5 }}>Adding these markers would improve accuracy. Consider requesting a full panel.</div>
          </div>
        </div>
      )}
    </div>
  );
}

/** One marker row in the Markers tab cards. `refText` of null/'' hides the
 *  reference cell; `badge` is a ready-made node (severity Badge or a note). */
function MarkerRow({ name, value, unit, refText, badge }) {
  return (
    <div className="rpt-mk">
      <div className="rpt-mk-name">{name}</div>
      <div className="rpt-mk-value">{value}{unit ? <small>{unit}</small> : null}</div>
      {refText ? <div className="rpt-mk-ref">{refText}</div> : <div />}
      <div className="rpt-mk-badge">{badge}</div>
    </div>
  );
}

// ── Tab 03 — Scored Markers (formerly folded into "All
//    Markers"/Tab 02 as a bolted-on subsection — split out into its own tab
//    so a reader isn't left guessing whether it's a subset of, or the same
//    list as, the Claude-scored markers above it) ─────────────────────────

/**
 * "Lab observations": the text of pages of the report that hold no numeric results (a culture report: "No growth", "Sterile after 2 days")
 * and that the navigator did not turn into markers. Shown as printed, never scored; the Markers tab is where a reader looks for what the
 * report said, so it sits there rather than in a separate tab. Renders nothing when there are none.
 * Staff-only: callers hide it in the patient view (readOnly).
 */
/**
 * The printed text of a page, laid out to read: one line per row, the first
 * short line as a heading, and the columns a scan separates with runs of
 * spaces shown as separated cells. The wording is the scan's, untouched.
 */
function ObservationText({ text }) {
  const lines = String(text || '').replace(/\r/g, '').split('\n').map((l) => l.trim());
  const rows = [];
  lines.forEach((l) => { if (l || (rows.length && rows[rows.length - 1] !== '')) rows.push(l); });
  while (rows.length && rows[rows.length - 1] === '') rows.pop();
  return (
    <div className="rpt-obs" style={{ gridColumn: '1 / -1', whiteSpace: 'pre-wrap' }}>
      {rows.map((l, i) => {
        if (!l) return <div key={i} className="rpt-obs-gap" />;
        const cells = l.split(/\s{2,}/);
        const isTitle = i === 0 && cells.length === 1 && l.length <= 60;
        return (
          <div key={i} className={isTitle ? 'rpt-obs-line rpt-obs-title' : 'rpt-obs-line'}>
            {cells.map((c, j) => <span key={j} className="rpt-obs-cell">{c}</span>)}
          </div>
        );
      })}
    </div>
  );
}

function LabObservations({ triage }) {
  const obs = (triage?.labObservations || []).filter((o) => o && String(o.text || '').trim());
  if (obs.length === 0) return null;
  return (
    <div style={{ marginTop: 14 }} data-testid="lab-observations">
      <div className="rpt-sh">
        <div className="rpt-st">Lab observations</div>
        <div className="rpt-ss">{obs.length} page{obs.length === 1 ? '' : 's'} · not scored</div>
      </div>
      <div className="rpt-panel">
        <div className="rpt-ref rpt-ref-plain" style={{ padding: '8px 12px' }}>
          Text from pages of the report that hold no numeric results (for example a culture report), shown as printed.
        </div>
        {obs.map((o) => (
          <div key={o.page} className="rpt-mrow">
            <div className="rpt-mn">Page {o.page}</div>
            <ObservationText text={o.text} />
          </div>
        ))}
      </div>
    </div>
  );
}

function DeterministicMarkersTab({ m3, triage, readOnly = false }) {
  // Which marker group is showing. null = "first group that has markers", so
  // the default is Out of Range when there is any, else Borderline, else
  // Optimal. A pick that stops existing (new data) falls back the same way.
  const [pickedGroup, setPickedGroup] = useState(null);
  // The engine formerly labelled "Model 3". V105: shown for review always;
  // whether it also drives THIS triage's consensus depends on the
  // per-triage model selection — see resolvedRiskModel()/model3() above.
  // When there is no evidence document, say so rather than rendering
  // nothing: a silently absent panel is indistinguishable from a broken
  // feature, and every triage evaluated before V95 is in that state.
  if (m3.scored.length === 0 && m3.unscored.length === 0) {
    return (
      <div>
        <div className="rpt-sh" style={{ marginTop:0 }}>
          <div className="rpt-st">Markers</div>
          <div className="rpt-ss">
            {triage.deterministicEvaluatedAt ? 'evaluated before evidence capture' : 'not evaluated'}
          </div>
        </div>
        <div className="rpt-panel" style={{ opacity:.85 }}>
          <div className="rpt-mrow">
            <div className="rpt-mn" style={{ color:'var(--rpt-ink-4)' }}>
              {triage.deterministicEvaluatedAt
                ? 'This report has a risk verdict but no marker list.'
                : 'The risk model did not run for this report.'}
            </div>
            <div className="rpt-ref" style={{ gridColumn:'2 / -1' }}>
              {triage.deterministicEvaluatedAt
                ? 'Its markers were not recorded at the time it was scored. The panel '
                  + 'appears on reports scored since, and on this one once an '
                  + 'administrator runs the evidence backfill.'
                : 'Nothing was recorded for this triage, so there is no verdict to '
                  + 'check and no markers to list.'}
            </div>
          </div>
        </div>
        {!readOnly && <LabObservations triage={triage} />}
      </div>
    );
  }

  return (
    <div>
      <div className="rpt-sh" style={{ marginTop:0 }}>
        <div className="rpt-st">Markers</div>
        <div className="rpt-ss">
          {m3.scored.length} scored · {Object.keys(m3.systems).length} system{Object.keys(m3.systems).length === 1 ? '' : 's'}
        </div>
      </div>

      {/* Constitution §"Recalibration Framework" score bands — internal
          reference for interpreting the "Deterministic Model NN" badge
          below and the per-system severity badges (both derived from the
          same 0.00–1.00 PREVIU_PRIORITY_SCORE, ×100 here to match what's
          on screen). Nav/Doctor only: a patient reading their own report
          should not be shown the internal scoring rubric, and this never
          reaches the exported PDF since triageReportPrint.js builds that
          section from its own separate HTML template, not this component. */}
      {!readOnly && (
        <div style={{
          background:'var(--rpt-info-tint)', border:'1px solid var(--rpt-info)',
          borderRadius:10, padding:'9px 14px', marginBottom:10,
          fontSize:11, color:'var(--rpt-info)', lineHeight:1.6,
        }}>
          <strong>Recalibration Framework score bands</strong> (internal reference,
          not shown to patients): GREEN 0&ndash;20 &middot; WATCHFUL 21&ndash;35 &middot;
          AMBER 36&ndash;55 &middot; RED 56&ndash;79 &middot; CRITICAL 80&ndash;100.
        </div>
      )}

      {Object.keys(m3.systems).length > 0 && (
        <div className="rpt-panel" style={{ marginBottom:10 }}>
          <div className="rpt-panel-head">
            <div className="rpt-panel-title">Organ Systems</div>
            {!readOnly && <Badge level={rl(triage.deterministicRiskLevel)} label={`Score ${triage.deterministicRiskScore ?? '—'}`} />}
          </div>
          {Object.entries(m3.systems).map(([sys, sev]) => (
            <div key={sys} className="rpt-mrow">
              <div className="rpt-mn">{systemDisplayName(sys)}</div>
              <div className="rpt-mv" />
              {/* Empty cell (not .rpt-ref, whose ::before adds a "ref " prefix):
                  keeps the badge in the same grid column as the marker rows. */}
              <div />
              <Badge level={sevBadgeLevel(sev)} label={sev} />
            </div>
          ))}
        </div>
      )}

      {(m3.scored.length > 0 || m3.unscored.some(m => m && m.reason === 'EXCLUDED_FROM_SCORING' && m.severity) || (!readOnly && m3.unscored.length > 0)) && (() => {
        // Grouped Out of Range / Borderline / Optimal rather than one flat
        // "Markers Behind the Score" list, and filtered so only one group's
        // card shows at a time. sevLevel() maps the Deterministic Model's
        // severity vocabulary onto the same high/moderate/low tiers Claude's
        // own bad/warn/good status uses.
        // A marker the registry classified but policy keeps out of the score (procalcitonin, blood / urine culture)
        // has a level: it is listed at that level, marked "not counted", rather than only under "Not scored".
        const isClassifiedUnscored = m => m && m.reason === 'EXCLUDED_FROM_SCORING' && m.severity;
        const levelled   = [...m3.scored, ...m3.unscored.filter(isClassifiedUnscored).map(m => ({ ...m, notCounted: true }))];
        const outOfRange = levelled.filter(m => sevLevel(m.severity) === 'high');
        const borderline = levelled.filter(m => sevLevel(m.severity) === 'moderate');
        const optimal    = levelled.filter(m => sevLevel(m.severity) === 'low');
        const unscored   = readOnly ? [] : m3.unscored.filter(m => !isClassifiedUnscored(m));

        const groups = [
          { key:'out',      title:'Out of Range',        items:outOfRange, color:'var(--rpt-bad)' },
          { key:'border',   title:'Borderline',          items:borderline, color:'var(--rpt-gold-deep)' },
          { key:'optimal',  title:'Optimal',             items:optimal,    color:'var(--rpt-good)' },
          { key:'unscored', title:'Shown but Not Scored', label:'Not scored', items:unscored, color:'var(--rpt-ink-4)' },
        ].filter(g => g.items.length > 0);
        const active = groups.find(g => g.key === pickedGroup) || groups[0];

        const M3Row = ({ m }) => (
          <MarkerRow
            name={(m.name || m.code || '—').toUpperCase()}
            value={m.value ?? '—'}
            unit={m.unit}
            refText={m.range || (m.system ? systemDisplayName(m.system) : '—')}
            badge={m.notCounted
              ? <span><Badge level={sevBadgeLevel(m.severity)} label={m.severity || '—'} /> <span style={{ fontSize:10.5, color:'var(--rpt-ink-4)' }}>not counted</span></span>
              : <Badge level={sevBadgeLevel(m.severity)} label={m.severity || '—'} />}
          />
        );

        // Shown, not scored. "We don't recognise this" and "policy forbids
        // this from scoring" are different statements to a clinician, and
        // the outgoing model made them indistinguishable.
        const UnscoredRow = ({ m }) => (
          <MarkerRow
            name={(m.name || m.code || '—').toUpperCase()}
            value={m.value ?? '—'}
            unit={m.unit}
            refText={UNSCORED_REASON[m.reason] || m.reason || '—'}
            badge={<span style={{ fontSize:10.5, color:'var(--rpt-ink-4)' }}>not counted</span>}
          />
        );

        if (!active) return null;
        return (
          <>
            <div className="rpt-filter" role="group" aria-label="Filter markers">
              {groups.map(g => (
                <button
                  key={g.key}
                  type="button"
                  className={`rpt-filter-btn${g.key === active.key ? ' active' : ''}`}
                  aria-pressed={g.key === active.key}
                  onClick={() => setPickedGroup(g.key)}
                >
                  {g.label || g.title} <span className="rpt-filter-n">{g.items.length}</span>
                </button>
              ))}
            </div>
            <div className="rpt-panel" style={{ marginBottom:10, ...(active.key === 'unscored' ? { opacity:.85 } : null) }}>
              <div className="rpt-panel-head">
                <div className="rpt-panel-title" style={{ color:active.color }}>{active.title}</div>
                <div className="rpt-panel-summary"><b>{active.items.length}</b> markers</div>
              </div>
              {active.key === 'unscored'
                ? active.items.map((m,i) => <UnscoredRow key={i} m={m} />)
                : active.items.map((m,i) => <M3Row key={i} m={m} />)}
            </div>
          </>
        );
      })()}

      {!readOnly && <LabObservations triage={triage} />}
    </div>
  );
}

// ── Tab 03 — Disease Risk ─────────────────────────────────────────────────────

function RiskTab({ triage, markers }) {
  // Disease Risk cards come from triageUtils.buildDiseaseRisks (the AI's per-disease estimates, else a marker-based fallback for
  // older records) so the Convergence tab and the PDF export read the same numbers.
  const claudeResult = parseJson(triage.claudeRawResponse);
  const organThreat  = claudeResult?.organ_threat;
  const { risks, fromClaude: buildFromClaude } = buildDiseaseRisks(triage, markers);

  // Organ threat banner — shown when v5.4 organ_threat.present = true
  const showOrganThreat = organThreat?.present === true;

  // Fallback if no data at all
  if (risks.length === 0) {
    return (
      <div className="rpt-placeholder">
        <div className="rpt-placeholder-icon">&#x1F4CA;</div>
        <div className="rpt-placeholder-title">Disease risk not available</div>
        <div className="rpt-placeholder-body">Insufficient marker data to compute individual disease risk scores. Please ensure marker data was extracted correctly.</div>
      </div>
    );
  }

  // Gap 2: Confidence level — use backend draw-count value when available.
  // Backend now sets triage.confidenceLevel = LOW/MODERATE/HIGH based on
  // number of prior draws (The Constitution §I.1).
  // Falls back to marker-count heuristic for legacy records without the field.
  const totalMarkers = markers.length;
  const confidenceLevel = triage.confidenceLevel
    ? triage.confidenceLevel                           // backend draw-count value (preferred)
    : totalMarkers >= 11 ? 'HIGH'                      // legacy fallback: marker count
    : totalMarkers >= 6  ? 'MODERATE'
    : totalMarkers >= 3  ? 'LOW'
    : 'INSUFFICIENT';
  const confColor = confidenceLevel === 'HIGH'         ? 'var(--rpt-good)'
                  : confidenceLevel === 'MODERATE'     ? '#b8843a'
                  : confidenceLevel === 'LOW'          ? 'var(--rpt-warn)'
                  :                                      'var(--rpt-bad)';
  // LOW confidence label includes draw context when backend value is present
  const confLabel = triage.confidenceLevel === 'LOW'
    ? `LOW CONFIDENCE · FIRST DRAW · ${totalMarkers} MARKERS`
    : `${confidenceLevel} CONFIDENCE · ${totalMarkers} MARKERS`;

  return (
    <div>
      {showOrganThreat && (
        <div style={{
          display:'flex', alignItems:'center', gap:8, marginBottom:12,
          background:'rgba(192,57,43,0.06)', border:'1px solid rgba(192,57,43,0.3)',
          borderLeft:'3px solid #c0392b', borderRadius:8, padding:'8px 12px',
        }}>
          <span style={{ fontSize:14 }}>🫀</span>
          <div>
            <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase',
              letterSpacing:'.08em', color:'#c0392b', fontFamily:'var(--rpt-fs)' }}>
              Organ threat confirmed — {organThreat.organ}
            </div>
            {organThreat.evidence && (
              <div style={{ fontSize:11, color:'var(--rpt-ink-3)', marginTop:2 }}>
                {organThreat.evidence}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="rpt-sh" style={{ marginTop:0 }}>
        <div className="rpt-st">Disease Risk <span className="a">
          {buildFromClaude ? '· AI-computed from report' : '· Estimated from markers'}
        </span></div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div className="rpt-ss">{risks.length} risk categories computed</div>
          <span style={{
            padding:'2px 9px', borderRadius:100, fontSize:10, fontWeight:700,
            letterSpacing:'0.5px', textTransform:'uppercase',
            background: confColor + '22', color: confColor,
            border: '1px solid ' + confColor + '50',
          }}>
            {confLabel}
          </span>
        </div>
      </div>
      <div className="rpt-risk-grid">
        {risks.map((r,i) => (
          <div key={i} className="rpt-risk-card">
            <div className="rpt-risk-head">
              <div>
                <div className="rpt-risk-window">{r.window}</div>
                <div className="rpt-risk-title">{r.name}</div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div className={`rpt-risk-pct`} style={{ color:riskColor(r.color) }}>
                  {r.pct}<sup>%</sup>
                </div>
                <Badge level={r.color} label={r.band} />
              </div>
            </div>
            <div style={{ fontSize:11, color:'var(--rpt-ink-4)', marginTop:3 }}>vs {r.baseline}</div>
            <div className="rpt-risk-body">{r.summary}</div>
            {r.contributors.length > 0 && (
              <div className="rpt-contrib">
                {r.contributors.map((c,j) => (
                  <span key={j} className={`rpt-badge ${c.w}`} style={{ fontSize:10 }}>{c.name}</span>
                ))}
              </div>
            )}
            {/* Change 4: Per-card Constitution disclaimer — AI Prohibition Zone */}
            <div style={{
              marginTop:10, paddingTop:8,
              borderTop:'1px solid var(--rpt-rule)',
              fontSize:10, color:'var(--rpt-ink-4)',
              fontStyle:'italic', lineHeight:1.5,
            }}>
              AI-estimated from available markers only. Not a clinical diagnosis.
              Physician confirmation required before any treatment decision.
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tab 04 — Trajectory ───────────────────────────────────────────────────────

// "Where it's heading": the intervention date and the prediction with
// and without action. Predictions need two draws at least MIN_PROJECTION_GAP_MONTHS
// apart; otherwise only the plan start is shown. Numbers come from
// buildHeadingInfo so the PDF quotes the same ones.
function HeadingBlock({ info, last, unit, yMin, yMax }) {
  const mon = (d) => d.toLocaleDateString('en-IN', { month:'short', year:'numeric' });
  const day = (d) => d.toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });
  const u = unit ? ` ${unit}` : '';
  const dec = info.decimals ?? (yMax < 20 ? 1 : 0);
  const f = (v) => Number(v).toFixed(dec);
  if (!info.canProject && !info.planStart) return null;

  const plan = info.planStart && (
    <div className="rpt-fc-plan"><i />Plan started {day(info.planStart)} · review by {mon(info.planEnd)}</div>
  );
  let body = null;
  if (!info.canProject) {
    body = (
      <div className="rpt-traj-note">
        Not enough history to project yet. Predictions appear once your first and last draws are at least {MIN_PROJECTION_GAP_MONTHS} months apart.
      </div>
    );
  } else if (info.same) {
    const improving = info.sqH !== last;
    body = (
      <div className="rpt-fc-same">
        <b>{f(info.sqH)}</b>
        <span>{unit} by {mon(info.endDate)} — {improving ? 'already moving the right way; expected to keep going with or without action.' : 'expected to hold steady with or without action.'}</span>
      </div>
    );
  } else {
    const delta = (v) => { const d = v - last; return `${d > 0 ? '▲' : '▼'} ${Math.abs(d).toFixed(dec)} ${d > 0 ? 'higher' : 'lower'}`; };
    body = (
      <>
        <div className="rpt-fc-tiles">
          <div className="rpt-fc-tile bad">
            <div className="rpt-fc-tile-l">Without action</div>
            <div className="rpt-fc-tile-v">{f(info.sqH)}<small>{unit}</small></div>
            <div className="rpt-fc-tile-d">By {mon(info.endDate)} · {delta(info.sqH)}</div>
          </div>
          <div className="rpt-fc-tile good">
            <div className="rpt-fc-tile-l">With action</div>
            <div className="rpt-fc-tile-v">{f(info.intH)}<small>{unit}</small></div>
            <div className="rpt-fc-tile-d">By {mon(info.endDate)} · {delta(info.intH)}</div>
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      <div className="rpt-fc-k">Where it's heading · by {info.endDate ? mon(info.endDate) : '24 months'}</div>
      {plan}
      {body}
    </>
  );
}

// Smooth area curve for a trajectory card: readings spaced evenly, joined by
// S-curves whose control points sit at the horizontal midpoint between
// neighbours (so it never overshoots a reading). Colours come from the card's
// rpt-traj-<tone> class.
function TrendCurve({ points }) {
  const W = 320, H = 110, T = 10, B = 6;
  const lo = Math.min(...points), hi = Math.max(...points);
  const span = hi === lo ? 1 : hi - lo;
  const step = W / (points.length - 1);
  const xy = points.map((v, i) => [i * step, T + (1 - (v - lo) / span) * (H - T - B)]);
  const line = xy.reduce((d, [x, y], i) => {
    if (i === 0) return `M${x},${y}`;
    const [px, py] = xy[i - 1], mx = (px + x) / 2;
    return `${d} C${mx},${py} ${mx},${y} ${x},${y}`;
  }, '');
  return (
    <svg className="rpt-traj-curve" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={`${points.length} readings`}>
      <path className="rpt-traj-area" d={`${line} L${W},${H} L0,${H} Z`} />
      <path className="rpt-traj-line" d={line} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// ── Trajectory card ───────────────────────────────────────────────────────────
//
// One card per prioritised marker: its current value, the SAME Sparkline the
// Today screen / Current Markers use for the real history (hover or tap a dot
// for its date and value), and a three-figure summary of where it is heading.
// This replaced a 920px custom SVG with axes, shaded zones and a four-row
// legend that was unreadable on a phone; the PDF still draws the full chart
// (triageReportPrint.js). The projections come from triageUtils so the card,
// the chart and the PDF quote the same numbers.
function TrajectoryCard({ markerName, unit, points, drawDates, interventionDate, yMin, yMax, threshold, band, velocity, slope, flagged = false, readOnly = false }) {
  const n = points.length;
  const last = points[n - 1];
  const higherIsBetter = isHigherBetter(markerName);
  const fmtDate = (d) => (d instanceof Date ? d : new Date(d))
    .toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });

  const worsening = higherIsBetter ? slope < 0 : slope > 0;
  const tone = band === 'bad' ? 'bad' : band === 'warn' ? 'warn' : 'good';
  const delta = n >= 2 ? Number((last - points[n - 2]).toFixed(2)) : null;

  const info = buildHeadingInfo({ markerName, drawDates, last, slope, threshold, yMin, yMax, interventionDate });

  return (
    <div className={`rpt-traj rpt-traj-${tone}`}>
      <div className="rpt-traj-head">
        <div>
          <div className="rpt-traj-name">{markerName}</div>
          <div className="rpt-traj-now">
            {last}{unit ? <small>{unit}</small> : null}
          </div>
        </div>
        {delta !== null && delta !== 0 && (
          <span className="rpt-traj-delta">{delta > 0 ? '↑' : '↓'} {Math.abs(delta)}</span>
        )}
      </div>
      {flagged && <div className="rpt-traj-flag">Flagged</div>}

      {n >= 2 ? (
        <>
          <TrendCurve points={points} />
          <div className="rpt-traj-foot">
            <span>{fmtDate(drawDates[0])}</span>
            <span className={`rpt-traj-trend${worsening ? ' worse' : ' better'}`}>Trend {velocity}</span>
            <span>{fmtDate(drawDates[n - 1])}</span>
          </div>
        </>
      ) : (
        <div className="rpt-traj-note">First reading for this marker. Add another report to see how it is changing.</div>
      )}

      <HeadingBlock info={info} last={last} unit={unit} yMin={yMin} yMax={yMax} />
    </div>
  );
}




// ── Tab 04 — Trajectory ───────────────────────────────────────────────────────

function TrajectoryTab({ triage, markers, history, healthRecord, patient, journey = null, readOnly = false }) {
  const score = triage.consensusRiskScore ?? 0;
  const hasHistory = history && history.length > 0;

  // The Health Score graph from the patient's Overview tab (same component, same journey data), staff only. If the journey could not be
  // loaded or has no scored report, the plain overall-trajectory card below stands in.
  const showScoreGraph = !!journey && (journey.milestones || []).some(m => m.score != null);

  // Debug: log history to confirm data is flowing (remove after verification)
  if (process.env.NODE_ENV !== 'production') {
    console.log('[Trajectory] history items:', history.length,
      history.map(h => ({
        id: h.id,
        drawDate: h.drawDate,
        createdAt: h.createdAt,
        hasClaudeRaw: !!h.claudeRawResponse,
        consensusScore: h.consensusRiskScore,
      }))
    );
  }

  // Chart data per marker from history + the current draw, computed by the same
  // function the PDFs use (triageUtils.buildMarkerChartData) so the trend, the
  // projections and the dates cannot drift between screen and report.
  // Draw dates are the real blood-draw dates (healthRecord.drawDate / h.drawDate),
  // not triage creation dates.
  const currentDrawDate = healthRecord?.drawDate
    ? new Date(healthRecord.drawDate)
    : new Date(triage.createdAt);
  const buildChartData = (markerName) => {
    const cd = buildMarkerChartData(
      markerName, markers, history, currentDrawDate,
      (h) => buildMarkers(parseJson(h.claudeRawResponse)),
    );
    if (!cd) return null;
    // V89 — the stored intervention date, set once by the first report drawn since
    // the patient registered and overridable by a navigator; null when none.
    return { ...cd, interventionDate: patient?.interventionStartDate || null };
  };

  // Markers the doctor flagged come first (all of them), then the markers with
  // the most clinical signal (bad/warn first) fill the list up to 4 in total.
  const coverage = trajectoryCoverage(markers, history, (h) => buildMarkers(parseJson(h.claudeRawResponse)));
  const gapNote = trajectoryGapNote(coverage);
  const flaggedMarkers = [];
  (triage.highlightedMarkers || []).forEach(h => {
    const m = coverage.multi.find(x => matchesMarkerName(x.name, h.markerName));
    if (m && !flaggedMarkers.includes(m)) flaggedMarkers.push(m);
  });
  const otherMarkers = [...coverage.multi]
    .filter(m => !flaggedMarkers.includes(m))
    .sort((a, b) => {
      const w = { bad: 0, warn: 1, good: 2 };
      return (w[a.status] ?? 1) - (w[b.status] ?? 1);
    })
    .slice(0, Math.max(0, 4 - flaggedMarkers.length));

  const overallHtml = overallTrajectoryHtml(buildOverallTrajectory(triage, history, currentDrawDate), {
    ink:'var(--rpt-ink)', muted:'var(--rpt-ink-4)', line:'var(--rpt-line)', dark:'var(--rpt-ink)', good:'var(--rpt-good)',
    goodBg:'var(--rpt-good-tint, #e0f0e5)', bad:'var(--rpt-bad)', badBg:'var(--rpt-bad-tint, #f7e7e4)', amber:'var(--rpt-gold-deep)',
    card:'var(--rpt-surface)', font:'var(--rpt-fs)',
  });

  const charts = [
    ...flaggedMarkers.map(m => ({ data: buildChartData(m.name), flagged: true })),
    ...otherMarkers.map(m => ({ data: buildChartData(m.name), flagged: false })),
  ].filter(c => c.data).map(c => ({ ...c.data, flagged: c.flagged }));

  return (
    <div>
      {/* The editorial intro ("A single result is a snapshot…" + the four
          capability icons) is PDF-only — see the Trajectory section in
          triageReportPrint.js. The screen goes straight to the charts. */}

      {/* Overall health trajectory — the Overview's score, drawn across draws as in the PDF design (staff only, like the Overview score). */}
      {!readOnly && showScoreGraph && (
        <div style={{ marginBottom:20 }}>
          <HealthScoreHero patient={patient} journey={journey} showGreeting={false} allowMetricToggle />
        </div>
      )}
      {!readOnly && !showScoreGraph && overallHtml && (
        <div style={{ marginBottom:20 }} dangerouslySetInnerHTML={{ __html: overallHtml }} />
      )}

      {/* Trajectory charts */}
      {charts.length > 0
        ? <div className="rpt-traj-grid">{charts.map((c, i) => <TrajectoryCard key={i} {...c} readOnly={readOnly} />)}</div>
        : gapNote ? null : (
          <div style={{ background:'var(--rpt-surface-2)', border:'1px solid var(--rpt-line)', borderRadius:11, padding:'20px', fontSize:13, color:'var(--rpt-ink-3)', lineHeight:1.6, textAlign:'center' }}>
            No numeric marker values could be parsed for chart generation.
          </div>
        )
      }
      {gapNote && <div className="rpt-traj-note" data-testid="trajectory-gap-note">{gapNote}</div>}
    </div>
  );
}


// ── Tab 05 — Convergence ──────────────────────────────────────────────────────

// riskMarkers: the full marker list. `markers` may have the optimal ones filtered out for display, but a risk estimate must not
// depend on that toggle (the denominator changes), so Disease Risk is always computed from everything.
function ConvergenceTab({ triage, markers, riskMarkers = markers }) {

  // The systems / patterns / root drivers / outcome model now comes from
  // triageUtils.buildConvergence, so the PDF export renders the same chain.
  // It was derived inline here, which the export could not reach — so the
  // export shipped a five-row system summary that appears nowhere in this UI,
  // and the exported report was a different document rather than a shorter one.
  const { systems: sysGroups, patterns: PATTERNS, drivers: ALL_DRIVERS,
          outcomes: OUTCOMES_MODEL, outOfRange, borderline } = buildConvergence(markers, buildDiseaseRisks(triage, riskMarkers).risks);

  const activeSysList = sysGroups.filter(s => s.markers.length > 0 || s.label === 'Sleep & Recovery');
  const activeDrivers = ALL_DRIVERS.filter(d => d.active);
  const ALL_OUTCOMES  = OUTCOMES_MODEL;
  const DISEASES      = OUTCOMES_MODEL;
  // buildConvergence has already resolved which outcomes an active driver feeds.
  const outcomeActive = (o) => !!o.active;

  // ── SVG flow layout — matching template exactly ───────────────────────────
  // Biomarkers: system groups with icons
  // Patterns: intermediate cluster pills
  // Root Drivers: driver pills in bordered box
  // Outcomes: circle icon + label + arrow
  const bioItems     = activeSysList;
  const patItems     = PATTERNS;
  const driverItems  = ALL_DRIVERS;
  const outcomeItems = ALL_OUTCOMES;

  const flowCols = [bioItems, patItems, driverItems, outcomeItems];
  const NCOLS = 4;
  const W = 900;
  const ROW_H = 48, ROW_GAP = 10;

  const colH = (ci) => flowCols[ci].length * (ROW_H + ROW_GAP);
  const H = Math.max(...flowCols.map((_,ci) => colH(ci))) + 40;

  const posY = (ci, ii) => {
    const arr = flowCols[ci];
    const total = arr.length * (ROW_H + ROW_GAP);
    const startY = (H - total) / 2;
    return startY + ii * (ROW_H + ROW_GAP) + ROW_H / 2;
  };
  const colW = W / NCOLS;

  const makePath = (fc, fi, tc, ti, color, thin=false) => {
    const x1 = fc * colW + colW * 0.78;
    const x2 = tc * colW + colW * 0.22;
    const y1 = posY(fc, fi);
    const y2 = posY(tc, ti);
    const dx = (x2 - x1) * 0.45;
    return { d:`M${x1.toFixed(1)} ${y1.toFixed(1)} C${(x1+dx).toFixed(1)} ${y1.toFixed(1)},${(x2-dx).toFixed(1)} ${y2.toFixed(1)},${x2.toFixed(1)} ${y2.toFixed(1)}`, color, thin };
  };

  // Build connectors
  const connectors = [];

  // Bio → Pattern
  bioItems.forEach((sys, bi) => {
    const sysHasIssue = sys.markers.some(m => m.status !== 'good');
    if (!sysHasIssue) return;
    patItems.forEach((p, pi) => {
      if (p.fromSys?.includes(sys.label)) {
        connectors.push(makePath(0, bi, 1, pi, sys.color, false));
      }
    });
    if (!patItems.some(p => p.fromSys?.includes(sys.label))) {
      connectors.push(makePath(0, bi, 1, 0, sys.color, false));
    }
  });

  // Pattern → Driver
  patItems.forEach((p, pi) => {
    driverItems.forEach((d, di) => {
      if (!d.active) return;
      if (d.patterns?.some(dp => dp === p.label)) {
        connectors.push(makePath(1, pi, 2, di, '#B08050', false));
      }
    });
  });

  // Driver → Outcome
  driverItems.forEach((d, di) => {
    if (!d.active) return;
    outcomeItems.forEach((o, oi) => {
      if (outcomeActive(o)) {
        const match = d.feeds.includes(o.name);
        if (match) connectors.push(makePath(2, di, 3, oi, '#B08050', true));
      }
    });
  });

  const colTitles = ['BIOMARKERS','PATTERNS','ROOT DRIVERS','AREAS AFFECTED'];
  const colSubs   = ['Multiple systems','Hidden connections',"What's really causing",'Where to look closer'];

  // outOfRange / borderline now come from buildConvergence above.

  return (
    <div>
      {/* Editorial header — full width, matching template */}
      <div style={{ marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
          <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.16em',
            color:'#FBF7EE', background:'var(--rpt-fill)', padding:'3px 9px', borderRadius:3,
            fontFamily:'var(--rpt-fs)' }}>TIER 02</div>
          <span style={{ color:'var(--rpt-line-strong)', fontSize:12 }}>|</span>
          <div style={{ fontSize:9.5, fontWeight:600, textTransform:'uppercase', letterSpacing:'.14em',
            color:'var(--rpt-gold-deep)', fontFamily:'var(--rpt-fs)' }}>CONVERGENCE INTELLIGENCE</div>
        </div>
        <div style={{ fontFamily:'var(--rpt-ff)', lineHeight:1.05, marginBottom:10 }}>
          <div style={{ fontSize:30, fontWeight:500, color:'var(--rpt-navy)' }}>Biomarkers don't move alone.</div>
          <div style={{ fontSize:30, fontWeight:500, color:'var(--rpt-gold-deep)', fontStyle:'italic' }}>We read the pattern beneath.</div>
        </div>
        <div style={{ width:48, height:2.5, background:'var(--rpt-gold)', borderRadius:2, marginBottom:16 }}/>

        {/* 3-col below headline: copy left, stats mid, callout right */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:24, alignItems:'start' }}>
          <div style={{ fontSize:13, color:'var(--rpt-ink-3)', lineHeight:1.65 }}>
            <p style={{ margin:'0 0 10px' }}>Your biomarkers do not change in isolation.</p>
            <p style={{ margin:'0 0 10px' }}>Sleep, inflammation, glucose and cardiovascular risk move together long before symptoms appear.</p>
            <p style={{ margin:0 }}>Previu identifies the <strong style={{ color:'var(--rpt-gold-deep)' }}>root drivers</strong> underneath the pattern.</p>
          </div>
          <div style={{ fontSize:13, color:'var(--rpt-ink-3)', lineHeight:1.8 }}>
            {outOfRange.length > 0 && <div><strong style={{ color:'var(--rpt-bad)' }}>{outOfRange.length} critical</strong>: {outOfRange.map(m=>m.name).join(', ')}</div>}
            {borderline.length > 0 && <div><strong style={{ color:'var(--rpt-gold-deep)' }}>{borderline.length} borderline</strong>: {borderline.map(m=>m.name).join(', ')}</div>}
            <div><strong style={{ color:'var(--rpt-good)' }}>{markers.filter(m=>m.status==='good').length} optimal</strong> · {markers.length} total markers</div>
            {activeDrivers.length > 0 && <div style={{ marginTop:6 }}><strong style={{ color:'var(--rpt-navy)' }}>{activeDrivers.length} root driver{activeDrivers.length!==1?'s':''}</strong> identified</div>}
          </div>
          {/* "Convergence is the earliest truth" callout */}
          <div style={{ display:'flex', alignItems:'flex-start', gap:12,
            background:'var(--rpt-surface)', border:'1px solid var(--rpt-line)',
            borderRadius:12, padding:'13px 15px' }}>
            <div style={{ width:36, height:36, borderRadius:'50%', background:'var(--rpt-gold-soft)',
              border:'1px solid var(--rpt-gold)', display:'grid', placeItems:'center',
              fontSize:14, color:'var(--rpt-gold-deep)', flexShrink:0 }}>⚡</div>
            <div>
              <div style={{ fontFamily:'var(--rpt-ff)', fontWeight:500, fontSize:14, color:'var(--rpt-navy)', marginBottom:4, lineHeight:1.2 }}>
                Convergence is<br/>the earliest truth.
              </div>
              <div style={{ fontSize:11.5, color:'var(--rpt-ink-3)', lineHeight:1.5 }}>
                The earliest signals are rarely isolated. They converge. Then they surface.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Convergence alert */}
      {activeDrivers.length >= 2 && (
        <div style={{ background:'linear-gradient(135deg,#8C2A2F,#B0432C)', color:'#FBF7EE',
          borderRadius:13, padding:'16px 20px', marginBottom:18,
          display:'grid', gridTemplateColumns:'auto 1fr', gap:14, alignItems:'start' }}>
          <div style={{ width:34,height:34,borderRadius:'50%',border:'1px solid #B0892C',
            display:'grid',placeItems:'center',color:'#B0892C',fontSize:15,flexShrink:0 }}>⚡</div>
          <div>
            <div style={{ fontSize:9.5,textTransform:'uppercase',letterSpacing:'.16em',color:'#B0892C',
              fontWeight:600,marginBottom:5,fontFamily:'var(--rpt-fs)' }}>
              CONVERGENCE ALERT · {activeDrivers.length} OF {ALL_DRIVERS.length} DRIVERS ACTIVE
            </div>
            <div style={{ fontFamily:'var(--rpt-ff)',fontStyle:'italic',fontSize:18,color:'#FBF7EE',marginBottom:5 }}>
              {activeDrivers.length} root drivers simultaneously elevated.
            </div>
            <div style={{ fontSize:12.5,color:'rgba(244,238,227,.80)',lineHeight:1.55 }}>
              Multi-system convergence across {[...new Set(activeDrivers.flatMap(d=>d.patterns||[]))].length} pattern clusters.
              Each driver reinforces the others — fixing one in isolation rarely holds.
            </div>
          </div>
        </div>
      )}

      {/* ── THE FLOW DIAGRAM ── */}
      <div style={{ background:'var(--rpt-surface)', border:'1px solid var(--rpt-line)',
        borderRadius:16, padding:'18px 20px 16px', marginBottom:16,
        position:'relative', overflow:'hidden' }}>

        {/* Column headers */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:0,
          marginBottom:10, position:'relative', zIndex:2 }}>
          {colTitles.map((t,i) => (
            <div key={i} style={{ paddingLeft: i===0 ? 0 : 6 }}>
              <div style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'.14em',
                color: i===2 ? 'var(--rpt-gold-deep)' : 'var(--rpt-navy)',
                fontWeight:700, marginBottom:3, fontFamily:'var(--rpt-fs)',
                display:'flex', alignItems:'center', gap:5 }}>
                {t}
                {i < 3 && <span style={{ color:'var(--rpt-ink-4)',fontSize:10,fontWeight:400 }}>›</span>}
              </div>
              <div style={{ fontFamily:'var(--rpt-ff)',fontStyle:'italic',
                fontSize:12.5,color:'var(--rpt-ink-3)' }}>{colSubs[i]}</div>
            </div>
          ))}
        </div>

        {/* Single SVG + absolute pills — exact template architecture */}
        <div style={{ position:'relative' }}>
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
            style={{ width:'100%', height:H, display:'block' }}>
            {connectors.map((c,i) => (
              <path key={i} d={c.d} fill="none" stroke={c.color}
                strokeWidth={c.thin ? 0.9 : 1.3}
                opacity={c.thin ? 0.45 : 0.40}
                strokeLinecap="round"/>
            ))}
          </svg>

          {/* Absolutely positioned item pills — same coordinate system as SVG */}
          <div style={{ position:'absolute', inset:0, pointerEvents:'none' }}>
            {flowCols.map((arr, ci) => (
              <div key={ci} style={{ position:'absolute',
                left:`${(ci/NCOLS)*100}%`, width:`${100/NCOLS}%`,
                top:0, bottom:0 }}>
                {arr.map((item, ii) => {
                  const yPct = (posY(ci, ii) / H) * 100;
                  const isO  = ci === 3;
                  const isD  = ci === 2;
                  const isB  = ci === 0;
                  const dActive  = isD && item.active;
                  const dInactive = isD && !item.active;

                  return (
                    <div key={ii} style={{
                      position:'absolute',
                      top:`calc(${yPct}% - ${ROW_H/2}px)`,
                      left: isO ? '6px' : '4px',
                      right: isO ? '4px' : '8px',
                      height: ROW_H,
                      opacity: dInactive ? 0.20 : 1,
                      pointerEvents:'auto',
                    }}>
                      {isB ? (
                        /* Biomarker system row — circle icon + label + subtitle */
                        <div style={{ display:'flex', alignItems:'center', gap:10,
                          height:'100%', paddingRight:6 }}>
                          <div style={{ width:38, height:38, borderRadius:'50%', flexShrink:0,
                            background:`${item.color}15`,
                            border:`1.5px solid ${item.markers.some(m=>m.status!=='good') ? item.color : 'var(--rpt-line)'}`,
                            display:'grid', placeItems:'center',
                            fontSize:16, color:item.color }}>
                            {item.icon}
                          </div>
                          <div style={{ minWidth:0 }}>
                            <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase',
                              letterSpacing:'.06em', color: item.markers.some(m=>m.status!=='good') ? item.color : 'var(--rpt-ink-3)',
                              fontFamily:'var(--rpt-fs)', lineHeight:1.1, marginBottom:1 }}>
                              {item.label}
                            </div>
                            <div style={{ fontSize:9.5, color:'var(--rpt-ink-4)', lineHeight:1.3,
                              overflow:'hidden', display:'-webkit-box',
                              WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
                              {item.markers.length > 0
                                ? item.markers.slice(0,3).map(m=>m.name).join(', ')
                                : item.sub.replace('\n',' ')}
                            </div>
                          </div>
                        </div>
                      ) : isO ? (
                        /* Outcome — circle icon + uppercase label + gold arrow */
                        <div style={{ display:'flex', alignItems:'center', gap:8, height:'100%' }}>
                          <div style={{ width:42, height:42, borderRadius:'50%', flexShrink:0,
                            background:'var(--rpt-surface)',
                            border:`1.5px solid ${outcomeActive(item) ? 'var(--rpt-line-strong)' : 'var(--rpt-line)'}`,
                            display:'grid', placeItems:'center',
                            fontSize:16,
                            color: outcomeActive(item) ? 'var(--rpt-navy)' : 'var(--rpt-ink-4)',
                            opacity: outcomeActive(item) ? 1 : 0.3 }}>
                            {item.icon}
                          </div>
                          <div style={{ fontSize:10.5, fontWeight:700,
                            color: outcomeActive(item) ? 'var(--rpt-navy)' : 'var(--rpt-ink-4)',
                            textTransform:'uppercase', letterSpacing:'.05em', lineHeight:1.3,
                            fontFamily:'var(--rpt-fs)', flex:1,
                            opacity: outcomeActive(item) ? 1 : 0.3 }}>
                            {/* Split label on \n — NO string replace, proper JSX */}
                            {item.label.split('\n').map((line,li) => (
                              <span key={li} style={{ display:'block' }}>{line}</span>
                            ))}
                          </div>
                          {outcomeActive(item) && (
                            <svg width="18" height="12" style={{ flexShrink:0 }}>
                              <line x1="0" y1="6" x2="11" y2="6" stroke="#B08050" strokeWidth="1.5"/>
                              <path d="M8 3 L13 6 L8 9" fill="none" stroke="#B08050" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          )}
                        </div>
                      ) : isD ? (
                        /* Root driver — circle icon + label lines */
                        <div style={{ display:'flex', alignItems:'center', gap:8,
                          height:'100%', paddingRight:4 }}>
                          <div style={{ width:36, height:36, borderRadius:'50%', flexShrink:0,
                            background: dActive ? `${item.color}18` : 'var(--rpt-bg-2)',
                            border:`1.5px solid ${dActive ? item.color : 'var(--rpt-line)'}`,
                            display:'grid', placeItems:'center',
                            fontSize:14, color: dActive ? item.color : 'var(--rpt-ink-4)' }}>
                            {item.icon}
                          </div>
                          <div style={{ minWidth:0 }}>
                            {item.label.split('\n').map((line,li) => (
                              <div key={li} style={{ fontSize:10.5, fontWeight:700,
                                color: dActive ? 'var(--rpt-navy)' : 'var(--rpt-ink-4)',
                                textTransform:'uppercase', letterSpacing:'.05em',
                                lineHeight:1.2, fontFamily:'var(--rpt-fs)' }}>
                                {line}
                              </div>
                            ))}
                            <div style={{ fontSize:9, color:'var(--rpt-ink-4)', marginTop:2,
                              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                              {item.v}
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* Pattern pill */
                        <div style={{ background:'var(--rpt-surface)',
                          border:'1px solid var(--rpt-line)',
                          borderLeft:`3px solid #B08050`,
                          borderRadius:7, boxShadow:'0 1px 3px rgba(26,40,65,.07)',
                          padding:'5px 8px', display:'flex', alignItems:'center',
                          justifyContent:'space-between', gap:5,
                          height:'100%', boxSizing:'border-box' }}>
                          <span style={{ fontSize:10.5, fontWeight:600, color:'var(--rpt-navy)',
                            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                            fontFamily:'var(--rpt-fs)' }}>
                            {item.label}
                          </span>
                          <span style={{ fontSize:9, color:'var(--rpt-ink-4)',
                            fontFamily:'var(--rpt-fm)', whiteSpace:'nowrap',
                            overflow:'hidden', textOverflow:'ellipsis', maxWidth:70, flexShrink:0 }}>
                            {item.v}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Highlighted box around Root Drivers column */}
          <div style={{ position:'absolute',
            left:`${(2/NCOLS)*100}%`, width:`${100/NCOLS}%`,
            top:0, bottom:0,
            border:'1px solid var(--rpt-line-strong)',
            borderRadius:10,
            background:'rgba(244,235,207,0.22)',
            pointerEvents:'none' }}/>
        </div>

        {/* System stream legend */}
        <div style={{ display:'flex', gap:14, marginTop:14, paddingTop:12,
          borderTop:'1px solid var(--rpt-line-soft)',
          fontSize:11, color:'var(--rpt-ink-3)', flexWrap:'wrap', fontFamily:'var(--rpt-fs)' }}>
          <span style={{ textTransform:'uppercase', letterSpacing:'.1em',
            color:'var(--rpt-ink-4)', fontWeight:600 }}>System streams:</span>
          {[['Inflammation','#B0432C'],['Glucose Metabolism','#3A7A4C'],
            ['Cardiovascular','#8C2A2F'],['Thyroid','#4A3A8A'],['Other','#7A7466']
          ].map(([label,color]) => (
            <span key={label} style={{ display:'inline-flex', alignItems:'center', gap:5 }}>
              <span style={{ width:14, height:2, background:color, borderRadius:1, display:'inline-block' }}/>
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* Root driver deep-dive cards — 2-col grid */}
      <div style={{ fontSize:11,textTransform:'uppercase',letterSpacing:'.14em',
        fontWeight:700,fontFamily:'var(--rpt-fs)',marginBottom:10 }}>
        <span style={{ color:'var(--rpt-navy)' }}>Root Drivers</span>
        <span style={{ color:'var(--rpt-gold-deep)' }}> · Deep dive</span>
        <div style={{ fontSize:12,color:'var(--rpt-ink-4)',textTransform:'none',letterSpacing:0,
          fontWeight:400,fontFamily:'var(--rpt-ff)',fontStyle:'italic',marginTop:2 }}>
          Each driver explained · contributing biomarkers · downstream pathways
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:12, marginBottom:16 }}>
        {ALL_DRIVERS.map((d,i) => (
          <div key={i} style={{ background:'var(--rpt-surface)', borderRadius:13, padding:'15px 17px',
            border:`1px solid ${d.active ? 'var(--rpt-gold)' : 'var(--rpt-line)'}`,
            boxShadow: d.active ? '0 2px 12px rgba(176,137,44,.10)' : 'none',
            opacity: d.active ? 1 : 0.35 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:7 }}>
              <div style={{ width:36,height:36,borderRadius:'50%',
                border:`1px solid ${d.active ? 'var(--rpt-gold)' : 'var(--rpt-line)'}`,
                background: d.active ? 'var(--rpt-gold-soft)' : 'var(--rpt-bg-2)',
                display:'grid',placeItems:'center',
                fontSize:15,color:'var(--rpt-gold-deep)',flexShrink:0 }}>
                {d.icon}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:'var(--rpt-ff)',fontWeight:500,fontSize:16,
                  color:'var(--rpt-navy)',letterSpacing:'-.005em',lineHeight:1.15 }}>
                  {d.label.split('\n').map((line,li) => (
                    <span key={li}>{line}{li < d.label.split('\n').length-1 ? ' ' : ''}</span>
                  ))}
                </div>
                <div style={{ fontFamily:'var(--rpt-fm)',fontSize:10,color:'var(--rpt-ink-4)',marginTop:1 }}>{d.v}</div>
              </div>
              <span className={`rpt-badge ${d.active ? 'warn' : 'info'}`}>{d.active ? 'Active' : 'Inactive'}</span>
            </div>
            <p style={{ fontSize:12,lineHeight:1.55,color:'var(--rpt-ink-2)',margin:'0 0 9px' }}>{d.detail}</p>
            <div style={{ fontSize:10,color:'var(--rpt-ink-3)',textTransform:'uppercase',
              letterSpacing:'.1em',marginBottom:5,fontFamily:'var(--rpt-fs)' }}>
              Feeds downstream into
            </div>
            <div style={{ display:'flex',flexWrap:'wrap',gap:4 }}>
              {d.feeds.map((f,j) => (
                <span key={j} className="rpt-badge warn" style={{ fontSize:9.5 }}>→ {f}</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Areas affected — plain-language read-out of the right-hand column of the diagram */}
      <div style={{ padding:'12px 14px', marginBottom:14, background:'var(--rpt-surface-2)',
        border:'1px solid var(--rpt-line)', borderRadius:9 }}>
        <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em',
          color:'var(--rpt-navy)', fontFamily:'var(--rpt-fs)', marginBottom:4 }}>
          Areas affected
        </div>
        <p style={{ fontSize:12, lineHeight:1.55, color:'var(--rpt-ink-3)', margin:'0 0 8px' }}>
          These are the parts of health this patient's abnormal markers connect to. A highlighted area is a place to look more
          closely, not a diagnosis. The Disease Risk tab has the estimated risk.
        </p>
        {OUTCOMES_MODEL.map(o => (
          <div key={o.key} style={{ display:'flex', gap:8, fontSize:12, lineHeight:1.5, padding:'2px 0',
            color: o.active ? 'var(--rpt-ink-2)' : 'var(--rpt-ink-4)' }}>
            <strong style={{ minWidth:170, color: o.active ? 'var(--rpt-navy)' : 'var(--rpt-ink-4)' }}>{o.name}</strong>
            <span>{o.why}</span>
          </div>
        ))}
      </div>

      {/* Pattern summary footer */}
      {markers.filter(m=>m.status!=='good').length > 0 && (
        <div style={{ padding:'10px 14px', background:'var(--rpt-surface-2)',
          border:'1px solid var(--rpt-line)', borderRadius:9,
          fontSize:12.5, color:'var(--rpt-ink-3)', lineHeight:1.6 }}>
          <strong style={{ color:'var(--rpt-navy)' }}>Pattern summary: </strong>
          {outOfRange.length > 0 && <span><strong style={{ color:'var(--rpt-bad)' }}>{outOfRange.length} critical</strong>: {outOfRange.map(m=>m.name).join(', ')}. </span>}
          {borderline.length > 0 && <span><strong style={{ color:'var(--rpt-gold-deep)' }}>{borderline.length} borderline</strong>: {borderline.map(m=>m.name).join(', ')}. </span>}
          {triage.claudeRecommendation && <span>{triage.claudeRecommendation}</span>}
        </div>
      )}
    </div>
  );
}


// ── Tab 06 — Your Action Items (internal id: 'actions'; formerly "Action Plan" —
//    renamed so it reads distinctly from the Clinical Detail tab below and
//    from the separate patient-facing /patient/care-plan page, see
//    PatientCarePlanPage.jsx) ──
// Persistent: checkbox state is saved to backend on every toggle (debounced 800ms).
// Backward compat: if triageResult.actionPlanJson is null (pre-V6 record),
// falls back to building the plan live from triage fields — same as the old behaviour.

function ActionPlanTab({ triage, missing, patientId, recordId, navigatorId, patient, drawDate, readOnly = false }) {
  const saveTimerRef = useRef(null);

  // Resolve action plan items — prefer stored (post-V6), fall back to live build
  const actions = React.useMemo(() => {
    if (triage?.actionPlanJson) return deserializeActionPlan(triage.actionPlanJson);
    return buildActionPlan(triage, missing);
  }, [triage, missing]);

  // Checkbox state — initialised from stored backend state
  const [done, setDone] = useState(() => deserializeCheckedState(triage?.actionPlanCheckedJson));

  // Persist checkbox state to backend — debounced 800ms
  const persistState = useCallback((newState) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      if (!patientId || !recordId) return;
      try {
        await healthRecordAPI.updateActionPlanChecks(patientId, recordId, JSON.stringify(newState));
      } catch (err) {
        // Non-blocking — navigator can keep working; state syncs on next open
        console.warn('[ActionPlanTab] Checkbox save failed:', err?.response?.data || err.message);
      }
    }, 800);
  }, [patientId, recordId]);

  const handleToggle = useCallback((itemId) => {
    if (readOnly) return; // Patient view: action plan is view-only, no navigator-checkbox writes
    setDone(prev => {
      const currentlyChecked = prev[itemId]?.checked || false;
      const newState = toggleCheckedState(prev, itemId, !currentlyChecked, navigatorId);
      persistState(newState);
      return newState;
    });
  }, [navigatorId, persistState, readOnly]);

  const groups = [
    { p:'P1', label:'Immediate', desc:'Act within 48 hours', cls:'p1', items: actions.filter(a=>a.priority==='P1') },
    { p:'P2', label:'Short-term', desc:'Within 2 weeks', cls:'p2', items: actions.filter(a=>a.priority==='P2') },
    { p:'P3', label:'Ongoing', desc:'Schedule and monitor', cls:'p3', items: actions.filter(a=>a.priority==='P3') },
  ].filter(g => g.items.length > 0);

  const checkedCount = Object.values(done).filter(v => v?.checked).length;

  // WhatsApp draft state
  const [showWaDraft, setShowWaDraft] = useState(false);
  const [waDraft, setWaDraft]         = useState('');
  const [waSending, setWaSending]     = useState(false);
  const [waSent, setWaSent]           = useState(false);

  const handleOpenWaDraft = useCallback(() => {
    const draft = buildWhatsAppDraft(patient, triage, actions, drawDate);
    setWaDraft(draft);
    setShowWaDraft(true);
  }, [patient, triage, actions, drawDate]);

  const handleSendWhatsApp = useCallback(async () => {
    setWaSending(true);
    const phone = patient?.phone || patient?.mobile || null;

    // 1. Send via WATI backend (session message — delivers in WhatsApp)
    if (phone) {
      try {
        await watiAPI.sendActionPlan(phone, patient?.firstName || 'Patient', waDraft);
        setWaSent(true);
        setTimeout(() => setWaSent(false), 3000);
      } catch (err) {
        // WATI send failed — still open WhatsApp Web as fallback
        console.warn('[WhatsApp] WATI send failed, falling back to wa.me:', err?.response?.data || err.message);
      }
    }

    // 2. Always open WhatsApp Web so navigator can confirm delivery
    openWhatsAppDraft(phone, waDraft);
    setWaSending(false);
  }, [patient, waDraft]);

  return (
    <div>
      <div className="rpt-sh" style={{ marginTop:0, marginBottom:14 }}>
        <div className="rpt-st">{readOnly ? 'Your Action Items' : 'Patient Action Items'} <span className="a">· {actions.length} steps</span></div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div className="rpt-ss">{checkedCount} of {actions.length} completed</div>
          {!readOnly && (
            <button
              type="button"
              className="rpt-wa-btn"
              onClick={handleOpenWaDraft}
              title="Draft a WhatsApp message for this patient"
            >
              <span>💬</span> WhatsApp Draft
            </button>
          )}
        </div>
      </div>

      {/* WhatsApp draft modal — navigator-only, never rendered in readOnly (patient) mode */}
      {!readOnly && showWaDraft && (
        <div className="rpt-wa-overlay" onClick={() => setShowWaDraft(false)}>
          <div className="rpt-wa-modal" onClick={e => e.stopPropagation()}>
            <div className="rpt-wa-modal-header">
              <div className="rpt-wa-modal-title">💬 WhatsApp Draft</div>
              <div className="rpt-wa-modal-sub">
                Review and edit before sending. Opens WhatsApp — you send it.
              </div>
            </div>
            <textarea
              className="rpt-wa-textarea"
              value={waDraft}
              onChange={e => setWaDraft(e.target.value)}
              rows={16}
              spellCheck
            />
            <div className="rpt-wa-modal-footer">
              <button type="button" className="rpt-wa-cancel-btn" onClick={() => setShowWaDraft(false)}>
                Cancel
              </button>
              <button type="button" className="rpt-wa-send-btn"
                onClick={handleSendWhatsApp} disabled={waSending}>
                {waSending ? 'Sending…' : waSent ? '✓ Sent!' : 'Open in WhatsApp →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {groups.map(g => (
        <div key={g.p} style={{ marginBottom:22 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
            <span className={`rpt-pb ${g.cls}`}>{g.p}</span>
            <div>
              <div style={{ fontFamily:'var(--rpt-ff)', fontSize:17, color:'var(--rpt-navy)', fontWeight:500 }}>{g.label}</div>
              <div style={{ fontSize:10.5, color:'var(--rpt-ink-4)' }}>{g.desc}</div>
            </div>
          </div>
          {g.items.map(a => {
            const isChecked = done[a.id]?.checked || false;
            const checkedAt = done[a.id]?.checkedAt;
            return (
              <div key={a.id} className="rpt-action" style={{ opacity: isChecked ? .55 : 1, cursor: readOnly ? 'default' : 'pointer' }}
                   onClick={() => handleToggle(a.id)}>
                <div
                  role={readOnly ? undefined : 'checkbox'} aria-checked={readOnly ? undefined : isChecked} tabIndex={readOnly ? -1 : 0}
                  onKeyDown={e => !readOnly && e.key === ' ' && handleToggle(a.id)}
                  style={{ width:22, height:22, borderRadius:'50%', border:`1.5px solid ${isChecked ? 'var(--rpt-gold)' : 'var(--rpt-line-strong)'}`, background: isChecked ? 'var(--rpt-gold)' : 'var(--rpt-surface)', cursor: readOnly ? 'default' : 'pointer', display:'grid', placeItems:'center', marginTop:2, flexShrink:0 }}>
                  {isChecked && <span style={{ color:'#fff', fontSize:12, fontWeight:700 }}>✓</span>}
                </div>
                <div>
                  <div className="rpt-acat">{a.category}</div>
                  <div className="rpt-atitle" style={{ textDecoration: isChecked ? 'line-through' : 'none' }}>{a.title}</div>
                  <div className="rpt-abody">{a.body}</div>
                  {isChecked && checkedAt && (
                    <div style={{ fontSize:11, color:'var(--rpt-good)', marginTop:4, fontWeight:500 }}>
                      ✓ Completed {new Date(checkedAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
                    </div>
                  )}
                  <div className="rpt-chips">{(a.tags || []).map((t,i) => <span key={i} className="rpt-chip">{t}</span>)}</div>
                </div>
                <div className="rpt-awin">Window<strong>{a.window}</strong></div>
              </div>
            );
          })}
        </div>
      ))}
      <div style={{ background:'var(--rpt-surface-2)', border:'1px solid var(--rpt-line)', borderRadius:11, padding:'12px 16px', marginTop:8, fontSize:12.5, color:'var(--rpt-ink-3)', lineHeight:1.55 }}>
        <strong style={{ color:'var(--rpt-navy)' }}>Note:</strong> AI-generated checklist. Always review with a qualified clinician before acting on recommendations.
      </div>
    </div>
  );
}

// ── PDF Download — dedicated print window ─────────────────────────────────────
// window.print() on the main page prints the whole app (nav sidebar etc).
// Instead we open a blank window, inject the report HTML + styles, and print that.

// buildPrintHtml lives in src/utils/triageReportPrint.js and is imported above.
//
// It used to be duplicated here — the util's own header said the two copies
// were "copied verbatim ... keep both copies in sync, or take on the (real, but
// riskier) refactor". They did not stay in sync, and the cost showed up as an
// exported PDF missing whole tabs. One copy now, so a section added to the
// report reaches both the Download PDF button and the patient-chat download.

// ── Main Modal ────────────────────────────────────────────────────────────────

// ── Tab 07 — Clinical Detail (internal id: 'careplan'; formerly "Care Plan" —
//    renamed to stop colliding with the separate patient-facing "Care Plan"
//    page at /patient/care-plan, PatientCarePlanPage.jsx, which is a
//    different feature: navigator-assigned action items, not Layers 11-16) ──
// Sep 2026: this is the second Claude call's output (claudeRawResponse.
// layers_11_18) — intervention_plan (Layer 11), clinical_briefing (Layer 12),
// safety_flags (Layer 13). Generated on every triage since the L11-18 split
// (2 Jul 2026) but never read by anything until now — see the two prompt
// files (triage-system-prompt-l11-18-intervention-briefing-v1.0.txt,
// ...-safety-monitoring-v1.0.txt) for the exact schema each field comes from.
//
// doctor_urgency/south_asian_risk_context (clinical_briefing) and
// red_flags_present/red_flag_details (safety_flags) are Java-injected after
// Claude's call returns — see TriageService.computeDoctorUrgency()/
// computeRedFlags(). The red-flag check here is informational only: the
// acute-alert banner patients/navigators actually see (OverviewTab's
// rpt-acute) comes from a separate mechanism, AcuteAlertDetector.
const DOCTOR_URGENCY_LEVEL = { IMMEDIATE: 'high', THIS_WEEK: 'moderate', NONE: 'low' };
const PRIORITY_LEVEL       = { P1: 'high', P2: 'moderate', P3: 'low' };
const CHANNEL_LABEL        = { NAVIGATOR_WHATSAPP: 'Navigator WhatsApp', IN_APP: 'In-app message', NONE: 'None' };

function CarePlanTab({ triage }) {
  const claudeResult = parseJson(triage.claudeRawResponse);
  const l11 = claudeResult?.layers_11_18 || null;

  if (!l11 || (!l11.intervention_plan && !l11.clinical_briefing && !l11.safety_flags && !l11.monitoring_protocol && !l11.memory_integration && !l11.communication_protocol)) {
    return (
      <div className="rpt-panel" style={{ opacity: .85 }}>
        <div className="rpt-mrow">
          <div className="rpt-mn" style={{ color: 'var(--rpt-ink-4)' }}>No clinical detail generated for this report.</div>
          <div className="rpt-ref" style={{ gridColumn: '2 / -1' }}>
            This triage predates the Layer 11-18 enrichment call, or that call failed and the report kept its Layer 9/10 verdict unchanged.
          </div>
        </div>
      </div>
    );
  }

  const intervention = l11.intervention_plan || {};
  const briefing      = l11.clinical_briefing || {};
  const safety         = l11.safety_flags || {};
  const monitoring      = l11.monitoring_protocol || {};
  const memory           = l11.memory_integration || {};
  const actions            = Array.isArray(intervention.navigator_actions) ? intervention.navigator_actions : [];
  const contraindications  = Array.isArray(safety.contraindications_noted) ? safety.contraindications_noted : [];
  const redFlagDetails     = Array.isArray(safety.red_flag_details) ? safety.red_flag_details : [];
  const repeatBiomarkers   = Array.isArray(monitoring.repeat_biomarkers) ? monitoring.repeat_biomarkers : [];
  const wearableParams     = Array.isArray(monitoring.wearable_parameters) ? monitoring.wearable_parameters : [];
  const symptomTriggers    = Array.isArray(monitoring.symptom_log_triggers) ? monitoring.symptom_log_triggers : [];
  const hasMonitoring      = repeatBiomarkers.length > 0 || wearableParams.length > 0 || symptomTriggers.length > 0;
  const commProtocol      = l11.communication_protocol || {};

  return (
    <div>
      {/* Intervention Plan — Layer 11 */}
      <div className="rpt-sh">
        <div className="rpt-st">Navigator Actions <span className="a">· Layer 11</span></div>
        <div className="rpt-ss">
          {actions.length} action{actions.length === 1 ? '' : 's'}
          {intervention.follow_up_recommended_days != null &&
            ` · Follow up in ${intervention.follow_up_recommended_days} day${intervention.follow_up_recommended_days === 1 ? '' : 's'}`}
        </div>
      </div>
      <div className="rpt-panel" style={{ marginBottom: 18, opacity: actions.length ? 1 : .85 }}>
        {actions.length > 0 ? (
          actions.map((a, i) => (
            <div key={i} className="rpt-mrow">
              <div className="rpt-mn">{a.action || '—'}</div>
              <div className="rpt-mv" style={{ fontSize: 11 }}>{a.timeframe || '—'}</div>
              <div className="rpt-ref" style={{ gridColumn: '3 / -1' }}>{a.rationale || '—'}</div>
              <Badge level={PRIORITY_LEVEL[a.priority] || 'unknown'} label={a.priority || '—'} />
            </div>
          ))
        ) : (
          <div className="rpt-mrow">
            <div className="rpt-mn" style={{ color: 'var(--rpt-ink-4)' }}>No navigator actions recommended.</div>
          </div>
        )}
      </div>

      {/* Clinical Briefing — Layer 12 */}
      {(briefing.headline || briefing.key_concern) && (
        <div className="rpt-ai" style={{ marginBottom: 18 }}>
          <div className="rpt-ai-glyph">🩺</div>
          <div>
            <div className="rpt-ai-lbl">Clinical Briefing <span style={{ opacity: .6 }}>· Layer 12</span></div>
            {briefing.headline && <div className="rpt-ai-title">{briefing.headline}</div>}
            {briefing.key_concern && <div className="rpt-ai-body">{briefing.key_concern}</div>}
            {briefing.doctor_urgency && briefing.doctor_urgency !== 'NONE' && (
              <div style={{ marginTop: 8 }}>
                <Badge level={DOCTOR_URGENCY_LEVEL[briefing.doctor_urgency] || 'unknown'}
                       label={`Doctor review: ${String(briefing.doctor_urgency).replace('_', ' ')}`} />
              </div>
            )}
            {briefing.south_asian_risk_context && (
              <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--rpt-ink-3)' }}>{briefing.south_asian_risk_context}</div>
            )}
          </div>
        </div>
      )}

      {/* Safety Screening — Layer 13 */}
      <div className="rpt-sh">
        <div className="rpt-st">Safety Screening <span className="a">· Layer 13</span></div>
        <div className="rpt-ss">
          {safety.red_flags_present ? 'Red flags present' : 'No red flags'}
          {safety.medication_review_needed != null && ` · Medication review ${safety.medication_review_needed ? 'needed' : 'not needed'}`}
        </div>
      </div>
      <div className="rpt-panel" style={{ marginBottom: 18 }}>
        {safety.red_flags_present && redFlagDetails.length > 0 && (
          <div className="rpt-missing" style={{ marginBottom: contraindications.length ? 10 : 0 }}>
            <span>🚩</span>
            <div>
              <strong>Red flags (Layer 13's own check — informational; any acute-alert banner above comes from a separate mechanism):</strong>
              <div style={{ marginTop: 4 }}>{redFlagDetails.join('; ')}</div>
            </div>
          </div>
        )}
        {contraindications.length > 0 ? (
          contraindications.map((c, i) => (
            <div key={i} className="rpt-mrow">
              <div className="rpt-mn">{c}</div>
              <div className="rpt-ref" style={{ gridColumn: '2 / -1' }}>Contraindication</div>
            </div>
          ))
        ) : (
          <div className="rpt-mrow">
            <div className="rpt-mn" style={{ color: 'var(--rpt-ink-4)' }}>No contraindications noted.</div>
          </div>
        )}
      </div>

      {/* Memory Integration — Layer 14 */}
      {memory.continuity_note && (
        <div style={{ marginBottom: 18 }}>
          <div className="rpt-sh"><div className="rpt-st">Continuity <span className="a">· Layer 14</span></div></div>
          <div className="rpt-card" style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--rpt-ink-2)' }}>
            {memory.continuity_note}
          </div>
        </div>
      )}

      {/* Communication Protocol — Layer 15, step 1: routing decision only,
          not yet wired to an actual send. See TriageService.computeCommunicationProtocol(). */}
      {commProtocol.channel && (
        <div style={{ marginBottom: 18 }}>
          <div className="rpt-sh"><div className="rpt-st">Communication Protocol <span className="a">· Layer 15</span></div></div>
          <div className="rpt-card" style={{ fontSize: 11.5, color: 'var(--rpt-ink-4)' }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span>Channel: <strong style={{ color: 'var(--rpt-ink-2)' }}>{CHANNEL_LABEL[commProtocol.channel] || commProtocol.channel}</strong></span>
              <span>Timing: <strong style={{ color: 'var(--rpt-ink-2)' }}>{commProtocol.send_immediately ? 'Immediate' : 'Queued'}</strong></span>
              {commProtocol.physician_notify && <span style={{ color: 'var(--rpt-warn)' }}>⚑ Physician notification recommended</span>}
            </div>
            <div style={{ marginTop: 6, fontStyle: 'italic' }}>Routing decision only — not yet wired to an automatic send.</div>
          </div>
        </div>
      )}

      {/* Monitoring Protocol — Layer 16 */}
      <div className="rpt-sh">
        <div className="rpt-st">Monitoring Protocol <span className="a">· Layer 16</span></div>
        <div className="rpt-ss">
          {monitoring.repeat_frequency_days != null
            ? `Repeat in ${monitoring.repeat_frequency_days} day${monitoring.repeat_frequency_days === 1 ? '' : 's'}`
            : null}
        </div>
      </div>
      <div className="rpt-panel" style={{ marginBottom: 18, opacity: hasMonitoring ? 1 : .85 }}>
        {repeatBiomarkers.length > 0 && (
          <div className="rpt-mrow">
            <div className="rpt-mn">Repeat biomarkers</div>
            <div className="rpt-ref" style={{ gridColumn: '2 / -1' }}>{repeatBiomarkers.join(', ')}</div>
          </div>
        )}
        {wearableParams.length > 0 && (
          <div className="rpt-mrow">
            <div className="rpt-mn">Wearable parameters</div>
            <div className="rpt-ref" style={{ gridColumn: '2 / -1' }}>{wearableParams.join(', ')}</div>
          </div>
        )}
        {symptomTriggers.length > 0 && (
          <div className="rpt-mrow">
            <div className="rpt-mn">Watch for</div>
            <div className="rpt-ref" style={{ gridColumn: '2 / -1' }}>{symptomTriggers.join(', ')}</div>
          </div>
        )}
        {!hasMonitoring && (
          <div className="rpt-mrow">
            <div className="rpt-mn" style={{ color: 'var(--rpt-ink-4)' }}>No monitoring protocol specified.</div>
          </div>
        )}
      </div>

      {intervention.patient_message_summary && (
        <div>
          <div className="rpt-sh"><div className="rpt-st">Patient Message</div></div>
          <div className="rpt-card" style={{ fontSize: 13.5, lineHeight: 1.65, color: 'var(--rpt-ink-2)' }}>
            {intervention.patient_message_summary}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab 07 — Lifestyle Recommendation ────────────────────────────────────────
// Displays the Layer 3 lifestyle recommendation generated async by
// LifestyleRecommendationService.java after each triage save.
//
// States:
//   1. Loading    — triage was just saved, async generation in progress
//   2. Available  — lifestyleRecommendation is populated
//   3. Unavailable — older record before Layer 3 was built (pre-Sprint 2)

// Sep 2026: lifestyle recommendations became 5-6 structured, individually
// doctor-reviewed points (LifestyleRecommendationItem) instead of one
// AI-written paragraph shown straight to the patient. This endpoint
// (healthRecordAPI.getTriageResult, shared by both the patient self-view and
// the navigator view — see TriageResultController) now only ever returns
// ACCEPTED points — a doctor reviews every point in the Doctor Portal's
// Lifestyle tab before it can appear here. triage.lifestyleRecommendation
// (the old paragraph) is left populated only on historical pre-migration
// rows, so it's kept as a read-only fallback rather than dropped outright.
//
// `audience` ('staff' default | 'patient') only changes copy, never data or
// polling — 'staff' is what TriageReportModal/DoctorTriageView already pass
// implicitly (by omitting the prop). 'patient' drops the "v5.7 Protocol"
// jargon badge and swaps the disclaimer: the default one reads "Navigator
// note: ... share with the patient during the call", which is instructions
// TO a navigator ABOUT the patient — showing that verbatim to the patient
// themselves (PatientLifestylePage) would read as a leaked internal note.
function LifestyleTab({ triage, patientId, recordId, audience = 'staff', onLoaded, standalone = false }) {
  const isPatient = audience === 'patient';
  const [liveItems, setLiveItems]   = useState(triage?.lifestyleItems || []);
  const [liveLegacy, setLiveLegacy] = useState(triage?.lifestyleRecommendation || null);
  const [liveGenAt, setLiveGenAt]   = useState(triage?.lifestyleGeneratedAt || null);
  // Points still waiting for a doctor, and the doctor looking after them (from the server).
  const [livePending, setLivePending]   = useState(triage?.lifestylePendingCount || 0);
  const [liveReviewer, setLiveReviewer] = useState(triage?.lifestyleReviewingDoctorName || null);
  const [liveStatus, setLiveStatus]     = useState(triage?.lifestyleStatus || null);
  const pollRef                     = useRef(null);
  const attemptsRef                 = useRef(0);
  const MAX_ATTEMPTS                = 12; // 12 × 10s = 2 minutes max polling

  const hasItems  = liveItems && liveItems.length > 0;
  const hasLegacy = !hasItems && liveLegacy && liveLegacy.trim().length > 0;

  // Checks the server while nothing approved has reached this view yet. The first
  // check runs on mount (the report may have been loaded before a doctor approved),
  // then every 10s. While generating, it gives up after 2 minutes (generation
  // finishes ~65s after triage save). While points are waiting for a doctor there
  // is no end to the wait, so it keeps checking (every 30s after the first 2
  // minutes) and also re-checks when the tab becomes visible again; otherwise an
  // approval made after this report was opened never showed up.
  const pendingRef = useRef(livePending);
  pendingRef.current = livePending;
  useEffect(() => {
    if (hasItems || hasLegacy) return;
    if (!patientId || !recordId) return;
    let cancelled = false;

    const check = async () => {
      try {
        const res = await healthRecordAPI.getTriageResult(patientId, recordId);
        if (cancelled) return;
        const items  = res?.data?.lifestyleItems;
        const legacy = res?.data?.lifestyleRecommendation;
        const pending = res?.data?.lifestylePendingCount || 0;
        pendingRef.current = pending;
        setLivePending(pending);
        setLiveReviewer(res?.data?.lifestyleReviewingDoctorName || null);
        setLiveStatus(res?.data?.lifestyleStatus || null);
        if (res?.data?.lifestyleGeneratedAt) setLiveGenAt(res.data.lifestyleGeneratedAt);
        if ((items && items.length > 0) || (legacy && legacy.trim().length > 0)) {
          setLiveItems(items || []);
          setLiveLegacy(legacy || null);
          setLiveGenAt(res?.data?.lifestyleGeneratedAt || null);
          // Lets the report's Download PDF include what this tab just fetched.
          if (onLoaded) onLoaded({
            lifestyleItems: items || [],
            lifestyleRecommendation: legacy || null,
            lifestyleGeneratedAt: res?.data?.lifestyleGeneratedAt || null,
          });
        }
      } catch {
        // Non-blocking — keep polling
      }
    };

    check();
    pollRef.current = setInterval(() => {
      attemptsRef.current += 1;
      const waitingOnDoctor = pendingRef.current > 0;
      if (attemptsRef.current > MAX_ATTEMPTS && !waitingOnDoctor) {
        clearInterval(pollRef.current);
        return;
      }
      // Past the 2-minute mark a doctor wait is checked every third tick (30s).
      if (attemptsRef.current > MAX_ATTEMPTS && attemptsRef.current % 3 !== 0) return;
      check();
    }, 10_000);

    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [patientId, recordId, hasItems, hasLegacy]); // eslint-disable-line react-hooks/exhaustive-deps

  const generatedAt = liveGenAt;
  const reviewers = lifestyleReviewers(liveItems);

  if (!hasItems && !hasLegacy) {
    // Points exist but none is approved yet: a doctor-review wait, not a generation wait,
    // so say who is reviewing instead of implying the AI is still working.
    if (livePending > 0 || generatedAt) {
      const who = liveReviewer || (isPatient ? 'Your doctor' : 'A doctor');
      return (
        <div className={`rpt-ls-empty${standalone ? ' rpt-ls-empty--standalone' : ''}`} aria-live="polite">
          <div className="rpt-ls-empty-icon">🩺</div>
          <div className="rpt-ls-empty-title">Lifestyle Recommendations Under Review</div>
          <div className="rpt-ls-empty-body">
            {isPatient
              ? <>{who} is reviewing the personalised recommendations prepared for this report.
                  Each one appears here, with their name, once approved.</>
              : <>Awaiting review by {liveReviewer || 'a doctor'}. Each recommendation appears here
                  once a doctor approves it.</>}
          </div>
        </div>
      );
    }
    // Generation failed (the server retries on its own, then gives up): say so honestly.
    if (liveStatus === 'FAILED' || liveStatus === 'FAILED_FINAL') {
      return (
        <div className={`rpt-ls-empty${standalone ? ' rpt-ls-empty--standalone' : ''}`} aria-live="polite">
          <div className="rpt-ls-empty-icon">🌱</div>
          <div className="rpt-ls-empty-title">Lifestyle Recommendations Taking Longer Than Usual</div>
          <div className="rpt-ls-empty-body">
            {liveStatus === 'FAILED'
              ? 'We are still preparing the personalised plan and will keep trying automatically.'
              : 'We could not prepare the plan automatically. Please ask your care team about it.'}
          </div>
        </div>
      );
    }
    return (
      <div className={`rpt-ls-empty${standalone ? ' rpt-ls-empty--standalone' : ''}`} aria-live="polite" aria-busy="true">
        <div className="rpt-ls-empty-icon">🌱</div>
        <div className="rpt-ls-empty-title">Lifestyle Recommendation Generating…</div>
        <div className="rpt-ls-empty-body">
          The personalised lifestyle plan is being prepared by the AI engine.
          This typically takes 90 seconds after a new triage is saved.
          This tab will update automatically — no need to reopen the report.
        </div>
        <div className="rpt-ls-empty-note">
          {attemptsRef.current > 0 && `Checking for update… (attempt ${attemptsRef.current}/${MAX_ATTEMPTS})`}
        </div>
      </div>
    );
  }

  return (
    <div className={`rpt-ls-root${standalone ? ' rpt-ls-root--standalone' : ''}`} aria-live="polite" aria-atomic="false">
      <div className="rpt-sh" style={{ marginTop:0, marginBottom:18 }}>
        <div className="rpt-st">
          Lifestyle Recommendations
          <span className="a"> · Personalised</span>
        </div>
        {generatedAt && (
          <div className="rpt-ss">
            Generated {new Date(generatedAt).toLocaleDateString('en-IN', {
              day: 'numeric', month: 'short', year: 'numeric',
            })}
          </div>
        )}
      </div>

      {/* Uses the shared .badge system (index.css) rather than the private
          rpt-ls-* pills, matching the AI/Doctor source badges Care Plan
          already shows (PatientCarePlanPage.jsx's AUTHOR_CONFIG). Every
          point reaching this view has already been through doctor review
          (see the file-level comment above) when hasItems is true. */}
      <div className="rpt-ls-badge-row">
        <span
          className="badge badge-purple"
          title={hasItems
            ? `AI-generated, reviewed and approved by ${reviewers.label || 'your doctor'}`
            : 'Generated automatically — not written by a doctor or navigator'}
        >
          {hasItems ? '🤖 AI Generated · ✓ Doctor Reviewed' : '🤖 AI Generated'}
        </span>
        {!isPatient && <span className="rpt-ls-badge rpt-ls-badge--v57">v5.7 Protocol</span>}
      </div>
      {hasItems && (reviewers.label || livePending > 0) && (
        <div className="rpt-ls-approved">
          {reviewers.label && (
            <div>
              <span aria-hidden="true">✓</span> Approved by <strong>{reviewers.label}</strong>
              {reviewers.latest ? ` · ${new Date(reviewers.latest).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
            </div>
          )}
          {livePending > 0 && (
            <div className="rpt-ls-approved-more">
              {livePending} more {livePending === 1 ? 'recommendation is' : 'recommendations are'} with {liveReviewer || (isPatient ? 'your doctor' : 'a doctor')} for review.
            </div>
          )}
        </div>
      )}

      {hasItems ? (
        <div className="rpt-ls-card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {liveItems.map((item, i) => (
            <div key={item.id || i} className="field-group">
              <div className="field-label">
                {item.domain}{item.timeWindow ? ` · ${item.timeWindow}` : ''}
              </div>
              <p className="rpt-ls-para" style={{ margin: 0, fontWeight: 500 }}>{item.habitChange}</p>
              {item.rationale && (
                <p className="rpt-ls-para" style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.85 }}>
                  {item.rationale}
                </p>
              )}
            </div>
          ))}
        </div>
      ) : (
        // Legacy fallback — a pre-migration triage run only ever has the old
        // single paragraph, never structured items.
        <div className="rpt-ls-card">
          {liveLegacy.split(/\n\n+/).map(p => p.trim()).filter(Boolean).map((para, i) => (
            <p key={i} className="rpt-ls-para">{para}</p>
          ))}
        </div>
      )}

      <div className="rpt-ls-disclaimer">
        {isPatient ? (
          <>This plan is generated automatically from your recent blood test results and reviewed
          by {reviewers.label || 'your doctor'} before appearing here. It's a helpful starting point — always check with
          your care team before making a major change.</>
        ) : (
          <><strong>Navigator note:</strong> These recommendations are AI-generated based on blood marker
          results and the v5.7 Intervention Prioritization protocol, and reviewed by your doctor before
          appearing here. Share with the patient during the navigator call. Clinical escalation decisions
          remain with the supervising physician.</>
        )}
      </div>
    </div>
  );
}

export default function TriageReportModal({
  triageResult, healthRecord, patient, triageHistory: rawTriageHistory = [], onClose, onMessagePatient, readOnly: readOnlyProp = false,
  onReevaluated,
  // Render inline as a normal page instead of a fixed overlay portalled to <body>.
  asPage = false,
}) {
  const [tab, setTab] = useState('overview');
  // Earlier draws only — never the report being viewed or another row for the same draw day.
  const triageHistory = priorDraws(rawTriageHistory, triageResult, healthRecord?.drawDate || triageResult?.drawDate || triageResult?.createdAt).slice(0, 8);
  // Lifestyle points the Lifestyle tab fetched after the report opened (see LifestyleTab's onLoaded).
  const [lifestyleLive, setLifestyleLive] = useState(null);
  // Staff (navigator/doctor) can preview the report exactly as the patient sees
  // it. The patient login passes readOnly itself and gets no toggle. Everything
  // downstream keys off the derived `readOnly`, so the preview is the real
  // patient render, not a lookalike.
  const [viewAsPatient, setViewAsPatient] = useState(true);
  const readOnly = readOnlyProp || viewAsPatient;
  // Optimal markers are hidden unless switched on; the same switch drives the PDF.
  const [showOptimal, setShowOptimal] = useState(false);
  // The patient's health journey: feeds the Health Score graph on the Trajectory tab and in the patient PDF. Optional: without it both
  // fall back to the plain overall-trajectory card.
  const [journey, setJourney] = useState(null);
  const journeyPatientId = patient?.id;
  useEffect(() => {
    if (!journeyPatientId) return undefined;
    let cancelled = false;
    healthJourneyAPI.get(journeyPatientId)
      .then(res => { if (!cancelled) setJourney(res?.data ?? null); })
      .catch(() => { if (!cancelled) setJourney(null); });
    return () => { cancelled = true; };
  }, [journeyPatientId]);
  const switchView = (asPatient) => { setViewAsPatient(asPatient); setTab('overview'); };
  // Read navigatorId here at modal level — always mounted, always inside AuthProvider context.
  // Passed down as prop to ActionPlanTab so it doesn't need its own useAuth() call.
  const { user } = useAuth();
  const navigatorId = user?.id || null;

  // Separate from navigatorId above: this one feeds PatientConfoundsTab's
  // recordedByStaffId, a real UUID column, and navigator JWTs carry the
  // account's EMAIL as `sub`, not a UUID (see currentNavigator.js) — the
  // same gap PatientDetailPage.js already had to work around. Resolved only
  // in navigator mode; readOnly (patient) mode has no staff id to resolve.
  const [confoundStaffId, setConfoundStaffId] = useState(null);
  useEffect(() => {
    if (readOnly) return;
    resolveNavigatorId(user).then(setConfoundStaffId).catch(() => setConfoundStaffId(null));
  }, [user, readOnly]);

  useEffect(() => { injectCss(); }, []);
  const { clinicalDetailEnabled } = useReportConfig();

  useEffect(() => {
    if (asPage) return undefined;
    const y = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${y}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, y);
    };
  }, [asPage]);

  useEffect(() => {
    if (asPage) return undefined;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, asPage]);

  const handleDownload = useCallback(() => {
    // Open a dedicated print window — never print the main app page
    const win = window.open('', '_blank', 'width=1000,height=700');
    if (!win) { alert('Pop-up blocked. Please allow pop-ups for this site and try again.'); return; }
    // The Lifestyle tab polls for points that arrive after the report opens;
    // fold in what it fetched so the PDF matches the screen.
    const printTriage = lifestyleLive ? { ...triageResult, ...lifestyleLive } : triageResult;
    const html = buildPrintHtml(reportId, patient, printTriage, allMarkers, allMissing,
                                healthRecord, triageHistory, m3Evidence, readOnly, showOptimal, journey);
    win.document.open();
    win.document.write(html);
    win.document.close();
    // Wait for fonts to load then print
    win.onload = () => { setTimeout(() => { win.focus(); win.print(); }, 800); };
  }, [triageResult, lifestyleLive, patient, healthRecord, readOnly, showOptimal, journey]); // eslint-disable-line

  if (!triageResult) return null;

  const claudeResult = parseJson(triageResult.claudeRawResponse);
  const allMarkers   = buildMarkers(claudeResult);
  // Display lists drop optimal markers unless the toggle is on.
  const markers      = showOptimal ? allMarkers : allMarkers.filter(m => m.status !== 'good');
  // Model 3's evidence (V95). Shape differs from rfFeatureFlags: an object
  // with scored/unscored/systems rather than a flat array, because a marker
  // that could NOT count is as informative to a reviewer as one that did.
  const m3Full       = parseModel3Evidence(triageResult.deterministicFeatureFlags, triageResult.claudeRawResponse);
  const notOptimal   = (m) => !m?.severity || sevLevel(m.severity) !== 'low';
  const m3Evidence   = showOptimal ? m3Full : {
    ...m3Full,
    scored:   (m3Full.scored   || []).filter(notOptimal),
    unscored: (m3Full.unscored || []).filter(notOptimal),
  };
  const missingC     = parseList(triageResult.claudeMissingMarkers);
  const missingRf    = parseList(triageResult.rfMissingKeys);
  // Deduplicate missing markers case-insensitively (e.g. "Troponin I" and "troponin" are the same)
  const _seen = new Set();
  const allMissing = [...missingC, ...missingRf].filter(m => {
    const key = m.toLowerCase().trim();
    if (_seen.has(key)) return false;
    _seen.add(key);
    return true;
  });

  const level  = rl(triageResult.deterministicRiskLevel);
  const initials = `${patient?.firstName?.[0]||''}${patient?.lastName?.[0]||''}`.toUpperCase();
  const fullName = `${patient?.firstName||''} ${patient?.lastName||''}`.trim() || 'Patient';
  const reportId = `PRV-${String(patient?.id||'').slice(0,8).toUpperCase()}-${new Date(triageResult.createdAt||Date.now()).toISOString().slice(0,10).replace(/-/g,'')}`;

  // Clinical Detail (Layer 11 "Navigator Actions" — internal escalation
  // directives written for a navigator/physician to act on, e.g. "Flag for
  // same-day physician review") is Nav/Doctor/Admin only, same as Claude
  // Model-Scored Markers, Disease Risk, Convergence and Confounds — all five
  // are internal clinical-detail views, not something a patient (or a
  // subscriber, who never reaches this modal at all today — their portal
  // only shows the compact TriageBody summary in
  // SubscriberBeneficiaryDetailPage.jsx) should see. readOnly is set
  // exclusively by PatientTriageResultPage, so gating on it here covers both
  // audiences without touching the navigator/doctor tab set at all.
  const ALL_TABS = readOnly
    ? [
        { id:'overview',      n:'01', label:'Overview' },
        { id:'deterministic', n:'02', label:'Markers' },
        { id:'trajectory',    n:'03', label:'Trajectory' },
        { id:'actions',       n:'04', label:'Action items' },
        { id:'lifestyle',     n:'05', label:'Lifestyle' },
      ]
    : [
        { id:'overview',      n:'01', label:'Overview' },
        { id:'deterministic', n:'02', label:'Markers' },
        { id:'risk',          n:'03', label:'Disease Risk' },
        { id:'trajectory',    n:'04', label:'Trajectory' },
        { id:'convergence',   n:'05', label:'Convergence' },
        { id:'actions',       n:'06', label:'Action items' },
        { id:'careplan',      n:'07', label:'Clinical Detail' },
        { id:'lifestyle',     n:'08', label:'Lifestyle' },
        { id:'confounds',     n:'09', label:'Confounds' },
      ];
  // The Trajectory tab compares this draw with earlier ones; with no earlier
  // draw there is nothing to show, so it is hidden for every audience. The
  // remaining tabs are renumbered so the strip never skips a number.
  const hasPriorDraws = Array.isArray(triageHistory) && triageHistory.length > 0;
  const TABS = ALL_TABS
    .filter(t => t.id !== 'trajectory' || hasPriorDraws)
    // Clinical Detail is switched by previu.report.clinical-detail-enabled on the server (off by default).
    .filter(t => t.id !== 'careplan' || clinicalDetailEnabled)
    .map((t, idx) => ({ ...t, n: String(idx + 1).padStart(2, '0') }));

  // Count on the Action items tab: same source ActionPlanTab renders from.
  const actionCount = (() => {
    try {
      return (triageResult?.actionPlanJson
        ? deserializeActionPlan(triageResult.actionPlanJson)
        : buildActionPlan(triageResult, allMissing)).length;
    } catch { return 0; }
  })();
  const status = { high:['bad','Review needed'], moderate:['warn','Monitor'], low:['good','On track'] }[rl(triageResult.consensusRiskLevel)];
  const metaBits = (
    <>
      {patient?.age && <span>{patient.age} years</span>}
      {patient?.age && <span className="rpt-dot" />}
      {patient?.gender && <span>{patient.gender}</span>}
      {patient?.bloodType && <><span className="rpt-dot" /><span>Blood {patient.bloodType}</span></>}
      <span className="rpt-dot" />
      <span>{/thyrocare/i.test(healthRecord?.labName || '') ? 'Lab Report' : (healthRecord?.labName || 'Lab Report')}</span>
    </>
  );

  const modal = (
    <div className={`rpt-overlay${asPage ? ' rpt-page' : ''}`}>
      <div className="rpt-shell">

        {!asPage && (
        <div className="rpt-chrome">
          <div className="rpt-brand">
            <div className="rpt-brand-mark">P</div>
            <div>
              <div className="rpt-brand-name">Previu</div>
              <div className="rpt-brand-sub">Health Intelligence Report</div>
            </div>
          </div>
          <div className="rpt-chrome-meta">
            <span>Draw date <strong>{fmtDate(healthRecord?.drawDate)}</strong></span>
            <div className="rpt-chrome-sep" />
            <span>Generated <strong>{fmtLong(triageResult.createdAt)}</strong></span>
          </div>
          <div className="rpt-chrome-actions">
            {!readOnlyProp && (
              <div role="group" aria-label="Report view" className="rpt-seg">
                <button type="button" className={`rpt-btn ${viewAsPatient ? 'primary' : ''}`} aria-pressed={viewAsPatient} onClick={() => switchView(true)}>Patient view</button>
                <button type="button" className={`rpt-btn ${viewAsPatient ? '' : 'primary'}`} aria-pressed={!viewAsPatient} onClick={() => switchView(false)}>Staff view</button>
              </div>
            )}
            <label style={{ display:'inline-flex', alignItems:'center', gap:6, marginRight:6, fontSize:12, cursor:'pointer' }}>
              <input type="checkbox" checked={showOptimal} onChange={e => setShowOptimal(e.target.checked)} />
              Show optimal markers
            </label>
            <button type="button" className="rpt-btn primary" onClick={handleDownload}>⬇ Download PDF</button>
            <button type="button" className="rpt-btn ghost" onClick={onClose} title="Close (Esc)">✕</button>
          </div>
        </div>
        )}

        {/* Scrollable content */}
        <div className="rpt-content">

          {asPage ? (
            <>
              <div className="rpt-hd">
                <div>
                  <div className="rpt-eyebrow">Clinical intelligence report</div>
                  <h1 className="rpt-title">Health report</h1>
                  <p className="rpt-sub">A concise overview of {fullName}&rsquo;s latest results</p>
                </div>
                <div className="rpt-hd-actions">
                  {!readOnlyProp && (
                    <div role="group" aria-label="Report view" className="rpt-seg">
                      <button type="button" className={`rpt-btn ${viewAsPatient ? 'primary' : ''}`} aria-pressed={viewAsPatient} onClick={() => switchView(true)}>Patient view</button>
                      <button type="button" className={`rpt-btn ${viewAsPatient ? '' : 'primary'}`} aria-pressed={!viewAsPatient} onClick={() => switchView(false)}>Staff view</button>
                    </div>
                  )}
                  <button type="button" className="rpt-btn-lg" onClick={handleDownload}>⤓ Export report</button>
                  {onMessagePatient && <button type="button" className="rpt-btn-lg primary" onClick={onMessagePatient}>💬 Message patient</button>}
                </div>
              </div>
              <div className="rpt-pcard">
                <div className="rpt-pcard-left">
                  <div className="rpt-avatar">{initials}</div>
                  <div>
                    <div className="rpt-pcard-k">Patient</div>
                    <div className="rpt-pname">{fullName}</div>
                    <div className="rpt-pmeta">{metaBits}</div>
                  </div>
                </div>
                <div className="rpt-pcard-right">
                  <div className="rpt-pcard-stat">
                    <div className="rpt-ss-l">Draw date</div>
                    <div className="rpt-ss-v">{fmtDate(healthRecord?.drawDate)}</div>
                  </div>
                  <div className="rpt-pcard-stat">
                    <div className="rpt-ss-l">Report generated</div>
                    <div className="rpt-ss-v">{fmtDate(triageResult.createdAt)}</div>
                  </div>
                  {status && <span className={`rpt-status ${status[0]}`}>{status[1]}</span>}
                </div>
              </div>
            </>
          ) : (
          <div className="rpt-ph">
            <div className="rpt-ph-left">
              <div className="rpt-avatar">{initials}</div>
              <div>
                <div className="rpt-pname">{fullName}</div>
                <div className="rpt-pmeta">
                  {patient?.age && <span>{patient.age}y</span>}
                  {patient?.age && <span className="rpt-dot" />}
                  {patient?.gender && <span>{patient.gender}</span>}
                  {patient?.bloodType && <><span className="rpt-dot" /><span>Blood {patient.bloodType}</span></>}
                  <span className="rpt-dot" />
                  <span>{/thyrocare/i.test(healthRecord?.labName || '') ? 'Lab Report' : (healthRecord?.labName || 'Lab Report')}</span>
                </div>
              </div>
            </div>
            {/* Staff see no risk score/ring here; only the patient view has a summary. */}
            {readOnly && (
              <div className="rpt-ph-right">
                <HealthScoreRing triage={triageResult} />
                <div className="rpt-ph-date">
                  <div className="rpt-ss-l">Blood draw date</div>
                  <div className="rpt-ss-v">{fmtLong(healthRecord?.drawDate)}</div>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Staff only: has the navigator / doctor reviewed this report yet? */}
          {!readOnlyProp && <ReportReviewStatus triageResultId={triageResult.id} />}

          {/* Tabs */}
          <div className="tabs-bar rpt-tabs">
            {TABS.map(t => (
              <button type="button" key={t.id} className={`tab-btn${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
                {t.label}
                {asPage && t.id === 'actions' && actionCount > 0 && <span className="rpt-tab-badge">{actionCount}</span>}
              </button>
            ))}
            {asPage && (
              <label className="rpt-tabs-right">
                <input type="checkbox" className="rpt-switch" checked={showOptimal} onChange={e => setShowOptimal(e.target.checked)} />
                Show optimal ranges
              </label>
            )}
          </div>

          {/* Tab panels */}
          <div className="rpt-tab-panel">
            {tab === 'overview'    && <OverviewTab    triage={triageResult} markers={markers} missing={allMissing} m3={m3Evidence} readOnly={readOnly}
              scoreGraph={journey && (journey.milestones || []).some(m => m.score != null)
                ? <HealthScoreHero patient={patient} journey={journey} showGreeting={false} allowMetricToggle={!readOnly} />
                : null} />}
            {tab === 'deterministic' && <DeterministicMarkersTab m3={m3Evidence} triage={triageResult} readOnly={readOnly} />}
            {tab === 'risk' && !readOnly && <RiskErrorBoundary><RiskTab triage={triageResult} markers={allMarkers} /></RiskErrorBoundary>}
            {tab === 'trajectory'  && <TrajectoryTab  triage={triageResult} markers={markers} history={triageHistory} healthRecord={healthRecord} patient={patient} journey={journey} readOnly={readOnly} />}
            {tab === 'convergence' && !readOnly && <ConvergenceTab triage={triageResult} markers={markers} riskMarkers={allMarkers} />}
            {tab === 'actions'     && <ActionPlanTab  triage={triageResult} missing={allMissing} patientId={patient?.id} recordId={healthRecord?.id} navigatorId={navigatorId} patient={patient} drawDate={healthRecord?.drawDate} readOnly={readOnly} />}
            {tab === 'careplan' && !readOnly && clinicalDetailEnabled && <CarePlanTab triage={triageResult} />}
            {tab === 'lifestyle'   && <LifestyleTab   triage={triageResult} patientId={patient?.id} recordId={healthRecord?.id} audience={readOnly ? 'patient' : 'staff'} onLoaded={setLifestyleLive} />}
            {tab === 'confounds' && !readOnly && (
              <PatientConfoundsTab
                patient={patient}
                currentUserId={confoundStaffId}
                source="NAVIGATOR"
                report={{ id: triageResult.id, drawDate: healthRecord?.drawDate }}
                onReevaluated={onReevaluated}
              />
            )}
          </div>

          {/* Footer */}
          <div className="rpt-footer">
            <span>🔒 Your data is private, secure, and protected.</span>
            <span>© 2026 Previu Health · AI-assisted report · For clinical use under guidance of a licensed provider</span>
          </div>
        </div>
      </div>
    </div>
  );

  return asPage ? modal : createPortal(modal, document.body);
}


// ── Named exports for doctor portal inline rendering ─────────────────────────
// These allow DoctorTriageView to render tab content directly without the
// portal overlay. Each export is the raw tab component — no modal chrome.
export {
  OverviewTab,
  DeterministicMarkersTab,
  LabObservations,
  RiskTab,
  TrajectoryTab,
  ConvergenceTab,
  ActionPlanTab,
  CarePlanTab,
  LifestyleTab,
  Badge,
  ScoreBar,
  BmCard,
  injectCss,
  buildMarkers,
  buildPrintHtml,
  parseJson,
  parseList,
  parseModel3Evidence,
  rl,
  sevLevel,
  riskColor,
  fmtDate,
  fmtLong,
};





